import React, { Component } from 'react';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import _ from 'lodash';
import moment from 'moment';
import { connect } from 'react-redux';

import actions from '../../../store/appActions';
import { createSelector } from 'reselect';

import MouseLocation from './MouseLocation';
import QueryBuilder from '../../../query-builder';
import { AGENT_LABEL_TEXT_FIELD } from '../../../utils/agentUtil';
import { getHourColor } from '../../../utils/trackColorUtil';
import { haversineMeters } from '../../../utils/locationUtil';
import { isTodayOrYesterdayRange } from '../../../utils/timeUtil';
import {
  shouldShowResultTag,
  getObservationTypeIcon,
  getObservationTypeLabel,
  getObservationSpriteMatchExpression
} from '../../../utils/observationTypeUtil';
import * as fullscreenHandlers from './mapComponent/fullscreen';
import * as graticuleHandlers from './mapComponent/graticule';
import * as mapInitHandlers from './mapComponent/mapInit';
import * as sourcesHandlers from './mapComponent/sources';
import * as zoomToAgentHandlers from './mapComponent/zoomToAgent';
import * as trackBoundsZoomHandlers from './mapComponent/trackBoundsZoom';
import * as actualTracksHandlers from './mapComponent/actualTracks';
import * as clustersHandlers from './mapComponent/clusters';
import * as observationClustersHandlers from './mapComponent/observationClusters';
import * as spiderHandlers from './mapComponent/spider';
import * as layerHandlers from './mapComponent/layers';
import * as heatmapHandlers from './mapComponent/heatmapLayer';
import * as tracksFullStandardHandlers from './mapComponent/tracksFullStandard';
import * as tracksFullColourHandlers from './mapComponent/tracksFullColour';
import MapPrint from './mapComponent/MapPrint';
import GraticuleOverlay from './mapComponent/GraticuleOverlay';

const normalizeTagLabel = value =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
// Per-layer config for the on-map heatmap toggle, keeping the recompute /
// toggle / visibility logic below generic across Observations and Tracks
// instead of duplicating it per entity type.
// Default heatmap fill opacity per layer (same values/reasoning as
// store/ontology/reducers.js - kept in sync manually since they live in
// different modules): Observations defaults to 35% transparency (opacity
// 0.65), Tracks to 60% transparency (opacity 0.4).
const DEFAULT_OBSERVATION_HEATMAP_OPACITY = 0.65;
const DEFAULT_TRACK_HEATMAP_OPACITY = 0.4;

const HEATMAP_LAYER_CONFIG = {
  Observations: {
    isShown: props => !!props.showObservationHeatmap,
    setShown: props => props.setShowObservationHeatmap
  },
  Tracks: {
    isShown: props => !!props.showTrackHeatmap,
    setShown: props => props.setShowTrackHeatmap
  }
};

const truncateTagLabel = value => {
  const text = (value || '').toString().trim();
  if (text.length <= 24) return text;
  return `${text.slice(0, 24)}...`;
};
const extractTagLabel = value => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'object') {
    const candidate = [value.label, value.name, value.value].find(
      item => item !== undefined && item !== null && String(item).trim()
    );
    return candidate ? String(candidate).trim() : '';
  }
  return String(value).trim();
};
const getResultTagLabels = item => {
  const labels = [];
  const seen = new Set();
  const pushUniqueLabel = value => {
    const label = extractTagLabel(value);
    if (!label || !shouldShowResultTag(label)) return;
    const dedupeKey =
      normalizeTagLabel(label) ||
      label
        .toString()
        .trim()
        .toLowerCase();
    if (!dedupeKey || seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    labels.push(truncateTagLabel(label));
  };
  const concepts = Array.isArray(item && item.concepts)
    ? item.concepts
    : item && item.concepts
    ? [item.concepts]
    : [];
  concepts.forEach(concept => {
    pushUniqueLabel(concept);
  });
  const tags = Array.isArray(item && item.tags)
    ? item.tags
    : item && item.tags
    ? [item.tags]
    : [];
  tags.forEach(tag => {
    pushUniqueLabel(tag);
  });
  const attributes = Array.isArray(item && item.attributes)
    ? item.attributes
    : [];
  attributes.forEach(attribute => {
    if (!attribute) return;
    const key = String(attribute.key || '')
      .trim()
      .toLowerCase();
    if (key === 'tags') {
      const values = Array.isArray(attribute.value)
        ? attribute.value
        : attribute.value
        ? [attribute.value]
        : [];
      values.forEach(pushUniqueLabel);
      return;
    }
    if (key === 'waterresource') {
      const value = String(attribute.value || '')
        .trim()
        .replace(/_/g, ' ');
      if (value) pushUniqueLabel(value);
    }
  });
  return labels;
};

class MapComponent extends Component {
  trackEndpointsSourceId = 'selectedTrackEndpoints';
  trackEndpointsLayerId = 'selectedTrackEndpoints';
  trackDirectionLayerId = 'actualTracks_direction';
  trackPlaybackSourceId = 'trackPlaybackPoint';
  trackPlaybackLayerId = 'trackPlaybackPoint';
  trackPlaybackProgressSourceId = 'trackPlaybackProgress';
  trackPlaybackProgressLayerId = 'trackPlaybackProgress';
  trackPlaybackObservationSourceId = 'trackPlaybackObservationHighlight';
  trackPlaybackObservationLayerId = 'trackPlaybackObservationHighlight';

  constructor(props) {
    super(props);
    this.state = {
      data: [],
      selected: '-',
      mapStyle: 'streets',
      selectedLayers: ['Observations', 'Tracks', 'Agents'],
      selectedDataSources: [],
      selectedAgents: [],
      concepts: [],
      timelineLength: 0,
      observationClickSet: false,
      alertClickSet: false,
      trackClickSet: false,
      agentClickSet: false,
      initialObservationLayerSet: false,
      initialTracksLayerSet: false,
      initialAgentsLayerSet: false,
      updateMapLayersCalled: false,
      isStyleLoaded: false,
      filterCollapsed: false,
      filterExpanded: false,
      resultsCollapsed: false,
      mode: true,
      mouseLat: '',
      mouseLong: '',
      loadingMapLayers: {},
      toastOpen: true,
      isMeasuring: false,
      distance: 'Measure distance',
      isMapFullscreen: false,
      isMapMaximizedFallback: false,
      playbackObservationToasts: [],
      playbackTrackInfo: null,
      fullTracksInfoDismissed: false,
      // Set to the track count at the moment 'Full tracks - colour coded'
      // got force-switched back to 'standard' for exceeding
      // MAX_COLOUR_CODED_TRACKS (see updateTracksFullColourLayer) - null
      // means no notice is showing. Dismissible; re-appears next time the
      // threshold is crossed again.
      colourModeFallbackNotice: null,
      // Same idea, for 'Track centerpoints' force-switching to
      // 'Track heatmap' above MAX_CENTERPOINT_TRACKS - see
      // enforceCenterpointsTrackLimit.
      centerpointsFallbackNotice: null,
      // "Full tracks - colour coded" hour-of-day range of interest (see the
      // dual-handle slider on the sky-gradient legend) - 0/24 means "no
      // filtering", every hour shows.
      trackHourRangeStart: 0,
      trackHourRangeEnd: 24,
      // Which hour-range handle is actively being dragged ('start' | 'end' |
      // null) - drives the live "12:00"-style tooltip shown only while
      // sliding, see startTrackHourDrag/onTrackHourDrag/endTrackHourDrag.
      draggingTrackHourHandle: null,
      // Mirrors actualTracks.js' isColourCoded (trackColor === 'colourCoded'
      // preference + exactly one track visible) - set via
      // setSingleTrackColourCoded so render() can show the same hour-colour
      // legend for a single selected/played-back track without
      // recalculating the trackColor+visible-track-count check here too.
      singleTrackColourCoded: false,
      // Dismissible independently of the toast itself - reset whenever
      // colour-coding newly turns on again (see setSingleTrackColourCoded),
      // so closing it for one track doesn't hide it forever.
      singleTrackColourLegendDismissed: false
    };
    this.map = null;
    this.mouseLocationRef = React.createRef();
    this.mapPaneRef = React.createRef();
    this.gettingLKP = false;
    this.skipNextMoveEndFetch = false;
    this.lastFetchedBounds = null;
    this.lastFetchedZoom = null;
    this.zoomToAgentFeature = null;
    this.clusterClickHandlersSet = new Set();
    this.spiderClickHandlersSet = false;
    this.overlapObservationGroups = {};
    this.observationClusterMembers = {};
    this._dirtyLayersSinceLastUpdate = null;
    this._pendingOverlapCallbackId = null;
    this.activeObservationSpiderKey = null;
    this._camtrapFetchPending = false;
    this.observationSourceClusterEnabled = null;
    this.observationClusterLayerFrontendMode = null;
    this.latestActualTracksData = null;
    this.actualTrackHoverSetup = false;
    this.pendingActualTracksFrame = null;
    this.pendingActualTracksFrameUsesRaf = false;
    this.pendingTrackBoundsZoom = false;
    this.trackBoundsZoomRetryTimer = null;
    this.trackBoundsZoomAttempts = 0;
    this.trackPlaybackPreparingTrackId = null;
    this.trackPlaybackState = {
      trackId: null,
      coordinates: [],
      pointTimestamps: [],
      segmentDurations: [],
      cumulativeSegmentDurations: [],
      cumulativeSegmentDistances: [],
      elapsedPlaybackMs: 0,
      totalPlaybackMs: 0,
      index: 0,
      completed: false,
      isPlaying: false,
      rafId: null,
      lastFrameTime: 0,
      msPerStep: 220,
      playbackSecondsPerRealMinute: 1,
      speedMultiplier: 1,
      observationEvents: [],
      firedObservationIds: new Set()
    };
    this.playbackObservationHighlightTimer = null;
    this.playbackObservationToastTimers = new Map();
    this.playbackObservationToastSequence = 0;
    this.timer = 0;
    this.LKPTimer = 0;
    this.startTimer = this.startTimer.bind(this);
    this.countDown = this.countDown.bind(this);
    this.geoJsonLine = {
      type: 'FeatureCollection',
      features: []
    };
    this.lineString = {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: []
      }
    };
  }

  componentDidMount() {
    this.startTimer();
    this.initiateComponent();
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    window.addEventListener('focus:zoom-to-agent', this.handleZoomToAgent);
    window.addEventListener(
      'focus:zoom-to-track-bounds',
      this.handleZoomToTrackBounds
    );
    window.addEventListener(
      'focus:zoom-to-track-item-bounds',
      this.handleZoomToTrackItemBounds
    );
    window.addEventListener(
      'focus:track-playback-control',
      this.handleTrackPlaybackControl
    );
  }

  componentWillUnmount() {
    this.stopTimer();
    this.stopLKPTimer();
    document.removeEventListener('pointermove', this.onTrackHourDrag);
    document.removeEventListener('pointerup', this.endTrackHourDrag);
    document.removeEventListener(
      'fullscreenchange',
      this.handleFullscreenChange
    );
    window.removeEventListener('focus:zoom-to-agent', this.handleZoomToAgent);
    window.removeEventListener(
      'focus:zoom-to-track-bounds',
      this.handleZoomToTrackBounds
    );
    window.removeEventListener(
      'focus:zoom-to-track-item-bounds',
      this.handleZoomToTrackItemBounds
    );
    window.removeEventListener(
      'focus:track-playback-control',
      this.handleTrackPlaybackControl
    );
    if (this.trackBoundsZoomRetryTimer) {
      clearTimeout(this.trackBoundsZoomRetryTimer);
      this.trackBoundsZoomRetryTimer = null;
    }
    this.stopTrackPlayback('stopped', false);
    if (this.playbackObservationHighlightTimer) {
      clearTimeout(this.playbackObservationHighlightTimer);
      this.playbackObservationHighlightTimer = null;
    }
    this.playbackObservationToastTimers.forEach(timerId =>
      clearTimeout(timerId)
    );
    this.playbackObservationToastTimers.clear();
    if (this.pendingActualTracksFrame !== null) {
      if (
        this.pendingActualTracksFrameUsesRaf &&
        typeof window !== 'undefined' &&
        typeof window.cancelAnimationFrame === 'function'
      ) {
        window.cancelAnimationFrame(this.pendingActualTracksFrame);
      } else {
        clearTimeout(this.pendingActualTracksFrame);
      }
      this.pendingActualTracksFrame = null;
      this.pendingActualTracksFrameUsesRaf = false;
    }
  }

  handleZoomToAgent = e => {
    return zoomToAgentHandlers.handleZoomToAgent.call(this, e);
  };

  handleFullscreenChange = () => {
    return fullscreenHandlers.handleFullscreenChange.call(this);
  };

  toggleMapMaximize = async () => {
    return fullscreenHandlers.toggleMapMaximize.call(this);
  };

  addGraticule = () => graticuleHandlers.addGraticule.call(this);
  removeGraticule = () => graticuleHandlers.removeGraticule.call(this);

  handleZoomToTrackBounds = () => {
    return trackBoundsZoomHandlers.handleZoomToTrackBounds.call(this);
  };

  handleZoomToTrackItemBounds = e => {
    return trackBoundsZoomHandlers.handleZoomToTrackItemBounds.call(this, e);
  };

  handleTrackPlaybackControl = e => {
    const detail = (e && e.detail) || {};
    const command = String(detail.command || '')
      .trim()
      .toLowerCase();
    if (command === 'stop') {
      this.stopTrackPlayback('stopped');
      return;
    }
    if (command === 'pause') {
      this.pauseTrackPlayback();
      return;
    }
    if (command === 'speed') {
      this.setTrackPlaybackSpeed(detail.speedMultiplier);
      return;
    }
    if (command === 'play') {
      this.startTrackPlayback(detail);
    }
  };

  getAgentIconImageExpression = () =>
    zoomToAgentHandlers.getAgentIconImageExpression.call(this);

  getObservationIconImageExpression = () =>
    getObservationSpriteMatchExpression({ includePhotoVariants: true });

  ensureZoomToAgentOverlayLayer = () => {
    return zoomToAgentHandlers.ensureZoomToAgentOverlayLayer.call(this);
  };

  updateZoomToAgentOverlay = () => {
    return zoomToAgentHandlers.updateZoomToAgentOverlay.call(this);
  };

  async componentDidUpdate(prevProps, prevState) {
    if (
      this.props.isMobile &&
      prevProps.mobileActive !== this.props.mobileActive &&
      this.props.mobileActive &&
      this.map
    ) {
      this.map.resize();
    }

    if (prevProps.mounted === false && this.props.mounted === true) {
      this.startLKPTimer();
    } else if (prevProps.mounted === true && this.props.mounted === false) {
      // Logged out (or logged out from under us, e.g. a 401 triggering
      // localLogout) - both timers otherwise keep firing forever with
      // nothing left to authenticate their calls, which was surfacing as a
      // repeating failed request every 20s after logout.
      this.stopTimer();
      this.stopLKPTimer();
    }

    if (prevProps.mapLayers.length === 0 && this.props.mapLayers.length > 0) {
      if (this.map.loaded()) {
        this.props.fetchAllData();
      }

      if (this.props.filterTreeFetched) {
        this.whenMapLayersLengthIsLargerThan0();
      }
    }

    if (
      prevProps.filterTreeFetched !== this.props.filterTreeFetched &&
      this.props.mapLayers.length > 0
    ) {
      this.whenMapLayersLengthIsLargerThan0();
    }

    if (prevProps.mapLayers !== this.props.mapLayers) {
      if (
        this.state.isStyleLoaded &&
        this.state.updateMapLayersCalled === false
      ) {
        await this.setMapSources();
      }
      if (this.props.mapLayers && prevProps.mapLayers) {
        this.props.mapLayers.forEach(mapLayer => {
          const prevMapLayer = prevProps.mapLayers.find(
            element => element.name === mapLayer.name
          );

          if (prevMapLayer && prevMapLayer.features !== mapLayer.features) {
            if (!this._dirtyLayersSinceLastUpdate) {
              this._dirtyLayersSinceLastUpdate = new Set();
            }
            this._dirtyLayersSinceLastUpdate.add(mapLayer.name);
            this.setState({
              loadingMapLayers: {
                ...this.state.loadingMapLayers,
                [mapLayer.name]: false
              }
            });

            if (mapLayer.name === 'Tracks') {
              this.updateTracksFullStandardLayer();
              this.updateTracksFullColourLayer();
              this.debouncedRefreshFullTrackTraces();
            }
          }
        });
      }
      this.updateMapLayers();
    }

    if (prevProps.showGrid !== this.props.showGrid && this.map) {
      if (this.props.showGrid) {
        this.addGraticule();
      } else {
        this.removeGraticule();
      }
    }

    if (
      prevProps.timelineTotal !== this.props.timelineTotal &&
      this.state.isStyleLoaded
    ) {
      this._dirtyLayersSinceLastUpdate = null; // process all layers on timeline change
      await this.setMapSources();
      this.updateMapLayers();
    }

    if (prevProps.trackLayer !== this.props.trackLayer && this.map) {
      if (
        this.map.getSource('actualTracks') &&
        this.map.getSource(this.trackEndpointsSourceId)
      ) {
        this.updateActualTracksLayers();
      }
      if (this.pendingTrackBoundsZoom) {
        this.tryZoomToTrackBounds(true);
        this.scheduleTrackBoundsZoomRetry();
      }
    }

    if (prevProps.fullTrackTraces !== this.props.fullTrackTraces) {
      this.updateTracksFullStandardLayer();
      this.updateTracksFullColourLayer();
    }

    if (
      prevProps.filterCollapsed !== this.props.filterCollapsed ||
      prevProps.filterExpanded !== this.props.filterExpanded ||
      prevProps.filterPanelWidthPx !== this.props.filterPanelWidthPx ||
      prevProps.resultsPanelWidthPx !== this.props.resultsPanelWidthPx ||
      prevProps.resultsCollapsed !== this.props.resultsCollapsed
    ) {
      this.map.resize();
    }

    if (prevProps.showAgentNames !== this.props.showAgentNames) {
      if (this.map && this.map.getLayer('Agents')) {
        const textField = this.props.showAgentNames
          ? AGENT_LABEL_TEXT_FIELD
          : '';
        this.map.setLayoutProperty('Agents', 'text-field', textField);
      }
    }

    if (prevProps.hideOldAgents !== this.props.hideOldAgents) {
      this.updateMapLayers();
    }

    if (prevProps.selectedAgents !== this.props.selectedAgents) {
      this.updateMapLayers();
    }

    if (
      prevProps.showObservationClusters !== this.props.showObservationClusters
    ) {
      // The observation fetch shape depends on this flag (server cluster
      // boxes via heatmap=5x5 vs individual points), so refetch the layer,
      // then rebuild its source/layers. Not gated on count: explicitly
      // choosing clusters must always cluster.
      const observationLayer = this.props.mapLayers.find(
        layer =>
          String(layer.projectId) === 'default' &&
          String(layer.name) === 'Observations'
      );
      if (observationLayer && this.props.getSpecificLayer) {
        this.props.getSpecificLayer(observationLayer);
      }
      this.rebuildObservationSourceAndLayers();
      this.updateMapLayers();
    }

    const prevHeatmaps = prevProps.heatmaps || {};
    const currHeatmaps = this.props.heatmaps || {};
    const prevSelectedLayers = prevProps.selectedLayers || [];
    const currSelectedLayers = this.props.selectedLayers || [];

    // Tracks turned off then back on (master show/hide checkbox, not just a
    // display-mode switch) -> re-show the Full tracks info panel even if it
    // was dismissed during the previous run.
    if (
      !prevSelectedLayers.includes('Tracks') &&
      currSelectedLayers.includes('Tracks')
    ) {
      this.setState({ fullTracksInfoDismissed: false });
    }

    Object.keys(HEATMAP_LAYER_CONFIG).forEach(layerName => {
      const config = HEATMAP_LAYER_CONFIG[layerName];
      const modeChanged =
        config.isShown(prevProps) !== config.isShown(this.props);
      const selectionChanged =
        prevSelectedLayers.includes(layerName) !==
        currSelectedLayers.includes(layerName);

      // Master show/hide (selection) and display-mode both change what shows;
      // re-apply the single visibility rule and (re)fetch when now active.
      if (modeChanged || selectionChanged) {
        this.applyHeatmapVisibility(layerName);
        if (
          config.isShown(this.props) &&
          currSelectedLayers.includes(layerName)
        ) {
          this.refreshHeatmap(layerName);
        }
      }

      // Push freshly fetched grid data into the map source.
      if (prevHeatmaps[layerName] !== currHeatmaps[layerName]) {
        this.syncHeatmapSource(layerName);
      }
    });

    // 'full' mode toggled directly (not via the heatmap flag) -> re-apply
    // the Tracks icon/line visibility split.
    if (prevProps.showTrackFullStandard !== this.props.showTrackFullStandard) {
      this.applyHeatmapVisibility('Tracks');
      if (this.props.showTrackFullStandard) {
        this.updateTracksFullStandardLayer();
        this.refreshFullTrackTraces();
        // Re-show the info panel each time full mode is (re-)enabled, even
        // if it was dismissed during a previous run.
        this.setState({ fullTracksInfoDismissed: false });
      }
    }

    // Same as above, for 'fullColour' mode.
    if (prevProps.showTrackFullColour !== this.props.showTrackFullColour) {
      this.applyHeatmapVisibility('Tracks');
      if (this.props.showTrackFullColour) {
        this.updateTracksFullColourLayer();
        this.refreshFullTrackTraces();
        this.setState({ fullTracksInfoDismissed: false });
      }
    }

    // Resolution changed while a heatmap is active -> refetch that layer at
    // its new grid size (obs and tracks are independent).
    if (
      prevProps.observationHeatmapResolution !==
      this.props.observationHeatmapResolution
    ) {
      this.refreshHeatmap('Observations');
    }
    if (
      prevProps.trackHeatmapResolution !== this.props.trackHeatmapResolution
    ) {
      this.refreshHeatmap('Tracks');
    }

    // Bounds/filter change refreshes layer features -> keep grids in sync.
    if (
      prevProps.mapLayers !== this.props.mapLayers &&
      Object.keys(HEATMAP_LAYER_CONFIG).some(name =>
        HEATMAP_LAYER_CONFIG[name].isShown(this.props)
      )
    ) {
      this.debouncedRefreshHeatmaps();
    }

    if (
      prevProps.camtrapResults !== this.props.camtrapResults &&
      this.props.camtrapResults.length > 0 &&
      prevProps.camtrapResults.length === 0
    ) {
      // Results arrived — fetch complete.
      this._camtrapFetchPending = false;
    }

    if (
      prevProps.camtrapResults !== this.props.camtrapResults &&
      this.props.camtrapResults.length === 0 &&
      prevProps.camtrapResults.length > 0 &&
      !this._camtrapFetchPending
    ) {
      // Results cleared without a pending fetch → user explicitly closed the panel.
      this.activeObservationSpiderKey = null;
      this.setClusterSelectionFilter(null);
    }

    if (prevProps.trackColor !== this.props.trackColor && this.map) {
      if (
        this.map.getSource('actualTracks') &&
        this.map.getSource(this.trackEndpointsSourceId)
      ) {
        this.updateActualTracksLayers();
      }
      this.refreshTrackPlaybackColors();
    }

    // 'Full tracks' is only meaningful over a short, recent window (see
    // isTodayOrYesterdayRange) - if the selected period moves away from
    // Today/Yesterday while it's the active mode, fall back to the default
    // centerpoints view rather than leaving an inaccessible-from-the-menu
    // mode running.
    if (
      (prevProps.queryStart !== this.props.queryStart ||
        prevProps.queryEnd !== this.props.queryEnd) &&
      this.getTrackDisplayMode() === 'full' &&
      !isTodayOrYesterdayRange(this.props.queryStart, this.props.queryEnd)
    ) {
      this.setTrackDisplayMode('centerpoints');
    }

    if (prevProps.selectedLanguage !== this.props.selectedLanguage) {
      await this.props.getFilterTreeList();
      await this.props.getLabels();
      this.props.fetchAllData();
    }

    if (this.props.alertTimelineOpen !== prevProps.alertTimelineOpen) {
      this.map.setLayoutProperty(
        'alert',
        'visibility',
        this.props.alertTimelineOpen ? 'visible' : 'none'
      );

      if (this.props.alertTimelineOpen) {
        this.props.clearSelectedItem();
        this.props.clearEntireTrack();

        // If alertTimelineOpen is true, hide all layers and store their previous visibility state
        this.prevLayerVisibility = this.props.selectedLayers.map(layer => {
          const visibility = this.map.getLayoutProperty(layer, 'visibility');
          this.map.setLayoutProperty(layer, 'visibility', 'none');
          return { layer, visibility };
        });

        // Update setSelectedLayers to an empty array as all layers are hidden
        this.props.setSelectedLayers(['Observations']);
      } else {
        // If alertTimelineOpen is false, restore the layers to their previous visibility state
        this.prevLayerVisibility.forEach(({ layer, visibility }) => {
          this.map.setLayoutProperty(layer, 'visibility', visibility);
        });

        // Update setSelectedLayers to include only layers that are visible
        const visibleLayers = this.prevLayerVisibility
          .filter(({ visibility }) => visibility === 'visible')
          .map(({ layer }) => layer);
        this.props.setSelectedLayers(visibleLayers);
      }
    }

    if (
      this.props.selectedItem !== prevProps.selectedItem &&
      this.props.selectedItem &&
      this.props.selectedItem.geometry &&
      this.props.selectedItem.geometry.coordinates &&
      this.props.selectedItem.properties &&
      this.props.selectedItem.properties.EntityType === 'user'
    ) {
      const [lng, lat] = this.props.selectedItem.geometry.coordinates;

      if (this.map && typeof lng === 'number' && typeof lat === 'number') {
        this.map.easeTo({
          center: [lng, lat],
          zoom: 12,
          duration: 500
        });
        if (this.map.getLayer('Agents')) {
          this.map.moveLayer('Agents');
        }
      }
    }

    const previousSelectedTrackId = this.getSelectedTrackId(
      prevProps.selectedItem
    );
    const currentSelectedTrackId = this.getSelectedTrackId(
      this.props.selectedItem
    );
    if (
      previousSelectedTrackId &&
      (!currentSelectedTrackId ||
        !this.trackIdsMatch(previousSelectedTrackId, currentSelectedTrackId))
    ) {
      this.setPlaybackTrackInfo(null);
    }
    if (previousSelectedTrackId !== currentSelectedTrackId) {
      if (
        this.map &&
        this.map.getSource('actualTracks') &&
        this.map.getSource(this.trackEndpointsSourceId)
      ) {
        this.updateActualTracksLayers();
      }
    }
  }

  getCurrentBounds = () => {
    return trackBoundsZoomHandlers.getCurrentBounds.call(this);
  };

  isBoundsWithin = (inner, outer) => {
    return trackBoundsZoomHandlers.isBoundsWithin.call(this, inner, outer);
  };

  getBoundsArea = bounds => {
    return trackBoundsZoomHandlers.getBoundsArea.call(this, bounds);
  };

  getBoundsCoverageRatio = (inner, outer) => {
    return trackBoundsZoomHandlers.getBoundsCoverageRatio.call(
      this,
      inner,
      outer
    );
  };

  getTrackBounds = trackLayer => {
    return trackBoundsZoomHandlers.getTrackBounds.call(this, trackLayer);
  };

  zoomToTrackWithContext = trackLayer => {
    return trackBoundsZoomHandlers.zoomToTrackWithContext.call(
      this,
      trackLayer
    );
  };

  tryZoomToTrackBounds = force => {
    return trackBoundsZoomHandlers.tryZoomToTrackBounds.call(this, force);
  };

  scheduleTrackBoundsZoomRetry = () => {
    return trackBoundsZoomHandlers.scheduleTrackBoundsZoomRetry.call(this);
  };

  initiateComponent = async () => {
    return mapInitHandlers.initiateComponent.call(this);
  };

  setupMap = async (userCoordinates = [0, 0], zoom = 2) => {
    return mapInitHandlers.setupMap.call(this, userCoordinates, zoom);
  };

  toggleMeasure = () => {
    this.map.fire('toggle-measure');
  };

  onRemove = () => {
    this.container.parentNode.removeChild(this.container);
    this.map = undefined;
  };

  whenMapLayersLengthIsLargerThan0 = async () => {
    await this.setMapSources();

    if (this.state.updateMapLayersCalled === false) {
      this.updateMapLayers();
    }
  };

  normalizeCompareValue = value =>
    (value || '')
      .toString()
      .trim()
      .toLowerCase();

  getSelectedAgentMatchValues = () => {
    const selectedAgents = Array.isArray(this.props.selectedAgents)
      ? this.props.selectedAgents
      : [];
    const values = new Set();
    selectedAgents.forEach(agent => {
      const nextValues = [
        agent && agent.name,
        agent && agent.value,
        agent && agent.displayLabel,
        agent && agent.id,
        agent && agent.EntityId,
        agent && agent.agentId
      ];
      nextValues.forEach(value => {
        const normalized = this.normalizeCompareValue(value);
        if (normalized) values.add(normalized);
      });
    });
    return values;
  };

  featureMatchesSelectedAgents = (feature, selectedAgentValues) => {
    if (!selectedAgentValues || selectedAgentValues.size === 0) return true;
    const props = (feature && feature.properties) || {};
    const agent = props.agent || {};
    const directAgent = (feature && feature.agent) || {};
    const candidates = [
      props.AgentId,
      props.agentId,
      props.AgentName,
      props.agentName,
      agent.agentId,
      agent.agentName,
      directAgent.agentId,
      directAgent.agentName
    ];
    const normalizedCandidates = candidates
      .map(candidate => this.normalizeCompareValue(candidate))
      .filter(Boolean);
    if (!normalizedCandidates.length) {
      // Keep features when agent identity is not present on the feature payload.
      return true;
    }
    return normalizedCandidates.some(candidate =>
      selectedAgentValues.has(candidate)
    );
  };

  getObservationFeatureId = feature => {
    const props = (feature && feature.properties) || {};
    return String(
      props.EntityId ||
        props.observationId ||
        props.ObservationId ||
        (feature && feature.observationId) ||
        (feature && feature.id) ||
        ''
    ).trim();
  };

  getTrackFocusedObservationIds = () => {
    const selectedItem = this.props.selectedItem || {};
    const isTrackFocusedSelection = Boolean(
      selectedItem.featureId ||
        (selectedItem.properties &&
          selectedItem.properties.EntityType === 'track')
    );
    if (!isTrackFocusedSelection) return null;

    const selectedTrackObservations = Array.isArray(
      this.props.timelineSelectedTrackObservations
    )
      ? this.props.timelineSelectedTrackObservations
      : [];
    if (!selectedTrackObservations.length) return null;
    const ids = new Set();
    selectedTrackObservations.forEach(item => {
      const observationId = String(
        item &&
          item.Observation &&
          (item.Observation.observationId || item.Observation.EntityId || '')
      ).trim();
      if (observationId) ids.add(observationId);
    });
    return ids.size ? ids : null;
  };

  getTrackFeatureId = feature => {
    const props = (feature && feature.properties) || {};
    return String(
      props.EntityId ||
        props.entityId ||
        props.FeatureId ||
        props.featureId ||
        props.trackId ||
        props.TrackId ||
        props.trackFeatureId ||
        props.TrackFeatureId ||
        (feature && feature.featureId) ||
        feature.id ||
        ''
    ).trim();
  };

  getSelectedTrackId = selectedItem => {
    if (!selectedItem || typeof selectedItem !== 'object') return null;
    const props = selectedItem.properties || {};
    const entityType = String(
      props.EntityType || selectedItem.featureType || ''
    ).toLowerCase();
    const isTrackEntity =
      entityType === 'track' || entityType.indexOf('track/') === 0;
    if (!isTrackEntity) return null;
    const trackId = this.getTrackFeatureId(selectedItem);
    return trackId || null;
  };

  normalizeTrackIdentifier = value =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/^track\//, '')
      .replace(/^urn:[^:]+:/, '');

  trackIdsMatch = (leftId, rightId) => {
    const left = this.normalizeTrackIdentifier(leftId);
    const right = this.normalizeTrackIdentifier(rightId);
    if (!left || !right) return false;
    return (
      left === right || left.endsWith(`/${right}`) || right.endsWith(`/${left}`)
    );
  };

  getTrackFocusedTrackIds = () => {
    const selectedTrackObservations = Array.isArray(
      this.props.timelineSelectedTrackObservations
    )
      ? this.props.timelineSelectedTrackObservations
      : [];
    if (!selectedTrackObservations.length) return null;
    const ids = new Set();
    selectedTrackObservations.forEach(item => {
      const observation = (item && item.Observation) || {};
      const props = observation.properties || {};
      const trackRef = observation.track || {};
      const candidates = [
        observation.trackId,
        observation.TrackId,
        observation.trackFeatureId,
        observation.TrackFeatureId,
        observation.parentTrackId,
        observation.parentFeatureId,
        observation.trackRef,
        observation.TrackRef,
        props.trackId,
        props.TrackId,
        props.trackFeatureId,
        props.TrackFeatureId,
        props.parentTrackId,
        props.parentFeatureId,
        props.trackRef,
        props.TrackRef,
        trackRef.featureId,
        trackRef.EntityId,
        trackRef.trackId
      ];
      const found = candidates.find(
        value =>
          value !== undefined && value !== null && String(value).trim() !== ''
      );
      if (found) {
        ids.add(String(found).trim());
      }
    });
    return ids.size ? ids : null;
  };

  emitTrackPlaybackStatus = (status, trackId = null) => {
    window.dispatchEvent(
      new CustomEvent('focus:track-playback-status', {
        detail: {
          status,
          trackId: trackId ? String(trackId) : null
        }
      })
    );
  };

  clearTrackPlaybackFrame = () => {
    const playback = this.trackPlaybackState;
    if (!playback || playback.rafId === null) return;
    if (
      typeof window !== 'undefined' &&
      typeof window.cancelAnimationFrame === 'function'
    ) {
      window.cancelAnimationFrame(playback.rafId);
    } else {
      clearTimeout(playback.rafId);
    }
    playback.rafId = null;
  };

  setTrackPlaybackPoint = coordinate => {
    if (!this.map || !this.map.getSource(this.trackPlaybackSourceId)) return;
    const feature =
      Array.isArray(coordinate) && coordinate.length >= 2
        ? [
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [Number(coordinate[0]), Number(coordinate[1])]
              },
              properties: {}
            }
          ]
        : [];
    this.map.getSource(this.trackPlaybackSourceId).setData({
      type: 'FeatureCollection',
      features: feature
    });
  };

  setTrackPlaybackProgress = (coordinates, index) => {
    if (
      !this.map ||
      !this.map.getSource(this.trackPlaybackProgressSourceId) ||
      !Array.isArray(coordinates)
    ) {
      return;
    }
    const clampedIndex = Math.max(
      0,
      Math.min(Number(index) || 0, coordinates.length - 1)
    );
    const pastCoordinates = coordinates.slice(0, clampedIndex + 1);
    const futureCoordinates = coordinates.slice(clampedIndex);
    const features = [];
    if (pastCoordinates.length >= 2) {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: pastCoordinates
        },
        properties: {
          progressState: 'past'
        }
      });
    }
    if (futureCoordinates.length >= 2) {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: futureCoordinates
        },
        properties: {
          progressState: 'future'
        }
      });
    }
    this.map.getSource(this.trackPlaybackProgressSourceId).setData({
      type: 'FeatureCollection',
      features
    });
  };

  zoomToPlaybackBounds = coordinates => {
    if (!this.map || !Array.isArray(coordinates) || coordinates.length < 2) {
      return;
    }
    const validCoordinates = coordinates.filter(
      coordinate =>
        Array.isArray(coordinate) &&
        coordinate.length >= 2 &&
        Number.isFinite(Number(coordinate[0])) &&
        Number.isFinite(Number(coordinate[1]))
    );
    if (validCoordinates.length < 2) return;

    let minLng = Number(validCoordinates[0][0]);
    let maxLng = Number(validCoordinates[0][0]);
    let minLat = Number(validCoordinates[0][1]);
    let maxLat = Number(validCoordinates[0][1]);
    validCoordinates.forEach(coordinate => {
      const lng = Number(coordinate[0]);
      const lat = Number(coordinate[1]);
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    });

    const canvas =
      this.map && typeof this.map.getCanvas === 'function'
        ? this.map.getCanvas()
        : null;
    const width = Math.max(0, Number(canvas && canvas.clientWidth) || 0);
    const height = Math.max(0, Number(canvas && canvas.clientHeight) || 0);
    const paddingHorizontal = Math.max(24, Math.round(width * 0.1));
    const paddingVertical = Math.max(24, Math.round(height * 0.1));

    this.skipNextMoveEndFetch = true;
    this.map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat]
      ],
      {
        padding: {
          top: paddingVertical,
          right: paddingHorizontal,
          bottom: paddingVertical,
          left: paddingHorizontal
        },
        duration: 550
      }
    );
  };

  setTrackPlaybackObservationHighlight = coordinate => {
    if (
      !this.map ||
      !this.map.getSource(this.trackPlaybackObservationSourceId)
    ) {
      return;
    }
    const feature =
      Array.isArray(coordinate) && coordinate.length >= 2
        ? [
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [Number(coordinate[0]), Number(coordinate[1])]
              },
              properties: {}
            }
          ]
        : [];
    this.map.getSource(this.trackPlaybackObservationSourceId).setData({
      type: 'FeatureCollection',
      features: feature
    });
  };

  resolveObservationCoordinate = observation => {
    if (!observation || typeof observation !== 'object') return null;
    const asCoordinate = value => {
      if (!Array.isArray(value) || value.length < 2) return null;
      const lng = Number(value[0]);
      const lat = Number(value[1]);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
      return [lng, lat];
    };
    const geometry = observation.geometry || {};
    if (geometry.type === 'Point') {
      return asCoordinate(geometry.coordinates);
    }
    const where = observation.where;
    if (
      where &&
      typeof where === 'object' &&
      Array.isArray(where.coordinates)
    ) {
      return asCoordinate(where.coordinates);
    }
    if (Array.isArray(where)) {
      if (Array.isArray(where[0])) return asCoordinate(where[0]);
      return asCoordinate(where);
    }
    const props = observation.properties || {};
    if (
      props.where &&
      typeof props.where === 'object' &&
      Array.isArray(props.where.coordinates)
    ) {
      return asCoordinate(props.where.coordinates);
    }
    if (Array.isArray(props.where)) {
      if (Array.isArray(props.where[0])) {
        return asCoordinate(props.where[0]);
      }
      return asCoordinate(props.where);
    }
    return null;
  };

  getObservationLinkedTrackId = observation => {
    const getAttributeValue = key => {
      const attributes = Array.isArray(observation && observation.attributes)
        ? observation.attributes
        : [];
      const entry = attributes.find(
        attribute =>
          attribute &&
          String(attribute.key || '')
            .trim()
            .toLowerCase() ===
            String(key || '')
              .trim()
              .toLowerCase()
      );
      return entry ? entry.value : null;
    };
    const props = (observation && observation.properties) || {};
    const trackRef = (observation && observation.track) || {};
    const candidates = [
      observation && observation.featureRef,
      observation && observation.trackId,
      observation && observation.TrackId,
      observation && observation.trackFeatureId,
      observation && observation.TrackFeatureId,
      observation && observation.parentTrackId,
      observation && observation.parentFeatureId,
      observation && observation.trackRef,
      observation && observation.TrackRef,
      props.trackId,
      props.TrackId,
      props.trackFeatureId,
      props.TrackFeatureId,
      props.parentTrackId,
      props.parentFeatureId,
      props.trackRef,
      props.TrackRef,
      trackRef.featureId,
      trackRef.EntityId,
      trackRef.trackId,
      getAttributeValue('trackId'),
      getAttributeValue('featureRef')
    ];
    const found = candidates.find(
      value =>
        value !== undefined && value !== null && String(value).trim() !== ''
    );
    return found ? String(found).trim() : null;
  };

  extractTrackUuid = value => {
    const text = String(value || '')
      .trim()
      .toLowerCase();
    if (!text) return '';
    const match = text.match(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/
    );
    return match ? match[0] : '';
  };

  trackIdsShareUuid = (leftId, rightId) => {
    const leftUuid = this.extractTrackUuid(leftId);
    const rightUuid = this.extractTrackUuid(rightId);
    if (!leftUuid || !rightUuid) return false;
    return leftUuid === rightUuid;
  };

  getObservationPlaybackEvents = (trackId, coordinates, pointTimestamps) => {
    if (!trackId || !Array.isArray(coordinates) || coordinates.length < 2) {
      return [];
    }
    const entries = Array.isArray(this.props.timelineSelectedTrackObservations)
      ? this.props.timelineSelectedTrackObservations
      : [];
    const observationCandidates = entries
      .map(entry => (entry && entry.Observation ? entry.Observation : entry))
      .filter(Boolean)
      .filter(observation => {
        const observationTrackId = this.getObservationLinkedTrackId(
          observation
        );
        if (!observationTrackId) return true;
        return (
          this.trackIdsMatch(observationTrackId, trackId) ||
          this.trackIdsShareUuid(observationTrackId, trackId)
        );
      });
    const scoreObservationData = observation => {
      if (!observation || typeof observation !== 'object') return 0;
      const badges = getResultTagLabels(observation);
      const description = String(observation.description || '').trim();
      const attributes = Array.isArray(observation.attributes)
        ? observation.attributes
        : [];
      let score = badges.length * 10;
      if (description) score += 3;
      if (attributes.length > 0) score += 1;
      if (observation.where || observation.geometry) score += 1;
      return score;
    };
    const bestObservationById = new Map();
    observationCandidates.forEach(observation => {
      const id = String(
        observation.observationId ||
          observation.ObservationId ||
          observation.EntityId ||
          (observation.properties && observation.properties.EntityId) ||
          ''
      ).trim();
      if (!id) return;
      const previous = bestObservationById.get(id);
      if (!previous) {
        bestObservationById.set(id, observation);
        return;
      }
      const previousScore = scoreObservationData(previous);
      const currentScore = scoreObservationData(observation);
      if (currentScore >= previousScore) {
        bestObservationById.set(id, observation);
      }
    });
    const observations = Array.from(bestObservationById.values());

    const getNearestIndex = coordinate => {
      let nearestIndex = 0;
      let minDistance = Number.POSITIVE_INFINITY;
      coordinates.forEach((point, index) => {
        const dx = Number(point[0]) - Number(coordinate[0]);
        const dy = Number(point[1]) - Number(coordinate[1]);
        const distance = dx * dx + dy * dy;
        if (distance < minDistance) {
          minDistance = distance;
          nearestIndex = index;
        }
      });
      return nearestIndex;
    };

    const events = observations
      .map(observation => {
        const id = String(
          observation.observationId ||
            observation.ObservationId ||
            observation.EntityId ||
            (observation.properties && observation.properties.EntityId) ||
            ''
        ).trim();
        if (!id) return null;
        const coordinate = this.resolveObservationCoordinate(observation);
        if (!coordinate) return null;
        const nearestIndex = getNearestIndex(coordinate);
        const whenMoment = observation.when ? moment(observation.when) : null;
        const traceTimestamp =
          Array.isArray(pointTimestamps) &&
          pointTimestamps[nearestIndex] != null
            ? Number(pointTimestamps[nearestIndex])
            : null;
        const timeLabel =
          whenMoment && whenMoment.isValid()
            ? whenMoment.format('HH:mm')
            : Number.isFinite(traceTimestamp)
            ? moment(traceTimestamp).format('HH:mm')
            : null;
        return {
          id,
          index: nearestIndex,
          coordinate,
          timeLabel,
          description:
            String(observation.description || '').trim() || 'No description',
          conceptBadges: getResultTagLabels(observation),
          observationType: getObservationTypeLabel(observation.observationType),
          observationTypeIcon: getObservationTypeIcon(
            observation.observationType
          )
        };
      })
      .filter(Boolean)
      .sort((left, right) => left.index - right.index);
    return events;
  };

  triggerObservationPlaybackEvent = event => {
    if (!event) return;
    this.setTrackPlaybackObservationHighlight(event.coordinate);
    if (this.playbackObservationHighlightTimer) {
      clearTimeout(this.playbackObservationHighlightTimer);
    }
    this.playbackObservationHighlightTimer = setTimeout(() => {
      this.setTrackPlaybackObservationHighlight(null);
      this.playbackObservationHighlightTimer = null;
    }, 2800);

    this.playbackObservationToastSequence += 1;
    const toastId = `${event.id}-${Date.now()}-${
      this.playbackObservationToastSequence
    }`;
    const nextToast = {
      id: toastId,
      description: event.description,
      conceptBadges: event.conceptBadges,
      timeLabel: event.timeLabel || null,
      observationType: event.observationType || 'Linked observation',
      observationTypeIcon: event.observationTypeIcon || ''
    };
    this.setState(prevState => ({
      playbackObservationToasts: [
        ...(prevState.playbackObservationToasts || []),
        nextToast
      ]
    }));
    const timerId = setTimeout(() => {
      this.playbackObservationToastTimers.delete(toastId);
      this.setState(prevState => ({
        playbackObservationToasts: (
          prevState.playbackObservationToasts || []
        ).filter(toast => toast && toast.id !== toastId)
      }));
    }, 4000);
    this.playbackObservationToastTimers.set(toastId, timerId);
  };

  triggerPlaybackObservationEventsUpToIndex = index => {
    const playback = this.trackPlaybackState;
    if (!playback || !Array.isArray(playback.observationEvents)) return;
    playback.observationEvents.forEach(event => {
      if (!event || playback.firedObservationIds.has(event.id)) return;
      if (event.index > index) return;
      playback.firedObservationIds.add(event.id);
      this.triggerObservationPlaybackEvent(event);
    });
  };

  formatPlaybackClockLabel = timestamp => {
    if (!Number.isFinite(Number(timestamp))) return null;
    return moment(Number(timestamp)).format('HH:mm');
  };

  formatElapsedTimeLabel = (startTimestamp, currentTimestamp) => {
    const start = Number(startTimestamp);
    const current = Number(currentTimestamp);
    if (!Number.isFinite(start) || !Number.isFinite(current)) return '--:--';
    const elapsedMs = Math.max(0, current - start);
    const elapsed = moment.duration(elapsedMs);
    const hours = Math.floor(elapsed.asHours())
      .toString()
      .padStart(2, '0');
    const minutes = elapsed
      .minutes()
      .toString()
      .padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  buildCumulativeSegmentDistances = coordinates => {
    const result = [0];
    for (let i = 1; i < coordinates.length; i++) {
      const prev = coordinates[i - 1];
      const curr = coordinates[i];
      if (
        !Array.isArray(prev) ||
        prev.length < 2 ||
        !Array.isArray(curr) ||
        curr.length < 2
      ) {
        result.push(result[result.length - 1]);
        continue;
      }
      const segmentKm = haversineMeters(prev, curr) / 1000;
      result.push(result[result.length - 1] + segmentKm);
    }
    return result;
  };

  calculateTrackSpeedKmh = (
    previousCoordinate,
    currentCoordinate,
    previousTimestamp,
    currentTimestamp
  ) => {
    if (
      !Array.isArray(previousCoordinate) ||
      previousCoordinate.length < 2 ||
      !Array.isArray(currentCoordinate) ||
      currentCoordinate.length < 2
    ) {
      return null;
    }
    const startTime = Number(previousTimestamp);
    const endTime = Number(currentTimestamp);
    if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) return null;
    const durationMs = endTime - startTime;
    if (durationMs <= 0) return null;
    const distanceKm =
      haversineMeters(previousCoordinate, currentCoordinate) / 1000;
    const hours = durationMs / 3600000;
    if (hours <= 0) return null;
    return distanceKm / hours;
  };

  setPlaybackTrackInfo = info => {
    this.setState(prevState =>
      !info && !prevState.playbackTrackInfo
        ? null
        : info &&
          prevState.playbackTrackInfo &&
          prevState.playbackTrackInfo.timeLabel === info.timeLabel &&
          prevState.playbackTrackInfo.elapsedLabel === info.elapsedLabel &&
          prevState.playbackTrackInfo.distanceLabel === info.distanceLabel &&
          prevState.playbackTrackInfo.speedLabel === info.speedLabel &&
          prevState.playbackTrackInfo.coordinateLabel === info.coordinateLabel
        ? null
        : { playbackTrackInfo: info }
    );
  };

  copyPlaybackTrackInfo = info => {
    if (!info) return;
    const speedText = String(info.speedLabel || '--').replace('.', ',');
    const copyText = `Clock time: ${info.timeLabel || '--:--'}
Elapsed time: ${info.elapsedLabel || '--:--'}
Speed: ${speedText} km/h
Location: ${info.coordinateLabel || '--, --'}`;
    this.copyTextToClipboard(copyText);
  };

  copyTextToClipboard = copyText => {
    const writeClipboard =
      navigator &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === 'function'
        ? navigator.clipboard.writeText(copyText)
        : null;
    if (writeClipboard && typeof writeClipboard.catch === 'function') {
      writeClipboard.catch(() => {});
      return;
    }
    if (typeof document === 'undefined') return;
    const helper = document.createElement('textarea');
    helper.value = copyText;
    helper.setAttribute('readonly', 'true');
    helper.style.position = 'absolute';
    helper.style.left = '-9999px';
    document.body.appendChild(helper);
    helper.select();
    try {
      document.execCommand('copy');
    } catch (error) {
      // Ignore copy errors because clipboard support is browser-dependent.
    }
    document.body.removeChild(helper);
  };

  syncPlaybackTrackInfoForIndex = (playback, index) => {
    const pointTimestamps = Array.isArray(playback.pointTimestamps)
      ? playback.pointTimestamps
      : [];
    const safeIndex = Math.max(
      0,
      Math.min(Number(index) || 0, playback.coordinates.length - 1)
    );
    const coordinate = Array.isArray(playback.coordinates[safeIndex])
      ? playback.coordinates[safeIndex]
      : null;
    const currentTimestamp = pointTimestamps[safeIndex];
    const firstTimestamp = pointTimestamps[0];
    const previousCoordinate =
      safeIndex > 0 && Array.isArray(playback.coordinates[safeIndex - 1])
        ? playback.coordinates[safeIndex - 1]
        : null;
    const previousTimestamp =
      safeIndex > 0 ? pointTimestamps[safeIndex - 1] : null;
    const timeLabel =
      this.formatPlaybackClockLabel(currentTimestamp) || '--:--';
    const elapsedLabel = this.formatElapsedTimeLabel(
      firstTimestamp,
      currentTimestamp
    );
    const speedKmh = this.calculateTrackSpeedKmh(
      previousCoordinate,
      coordinate,
      previousTimestamp,
      currentTimestamp
    );
    const coordinateLabel =
      Array.isArray(coordinate) && coordinate.length >= 2
        ? `${Number(coordinate[1]).toFixed(5)}, ${Number(coordinate[0]).toFixed(
            5
          )}`
        : '--, --';
    const speedLabel =
      Number.isFinite(Number(speedKmh)) && Number(speedKmh) >= 0
        ? Number(speedKmh).toFixed(1)
        : '--';
    const speedDisplay = String(speedLabel).replace('.', ',');
    const cumulativeDistances = Array.isArray(
      playback.cumulativeSegmentDistances
    )
      ? playback.cumulativeSegmentDistances
      : [];
    const elapsedKm = cumulativeDistances[safeIndex];
    let distanceLabel = '--';
    if (Number.isFinite(elapsedKm)) {
      distanceLabel =
        elapsedKm < 1
          ? `${Math.round(elapsedKm * 1000)} m`
          : `${elapsedKm.toFixed(1)} km`;
    }
    this.setPlaybackTrackInfo({
      timeLabel,
      elapsedLabel,
      distanceLabel,
      speedLabel,
      speedDisplay,
      coordinateLabel
    });
  };

  setTrackPlaybackSpeed = speedMultiplierRaw => {
    const playback = this.trackPlaybackState;
    const parsedMultiplier = Number(speedMultiplierRaw);
    const normalizedMultiplier =
      Number.isFinite(parsedMultiplier) && parsedMultiplier > 0
        ? parsedMultiplier
        : 1;
    playback.speedMultiplier = normalizedMultiplier;
    if (
      !playback.trackId ||
      !Array.isArray(playback.coordinates) ||
      playback.coordinates.length < 2
    ) {
      return;
    }
    const segmentDurations = this.buildPlaybackSegmentDurations(
      playback.pointTimestamps,
      playback.coordinates.length,
      normalizedMultiplier
    );
    const cumulativeSegmentDurations = [];
    let cumulativeDuration = 0;
    segmentDurations.forEach(value => {
      cumulativeDuration += Number(value) || 0;
      cumulativeSegmentDurations.push(cumulativeDuration);
    });
    playback.segmentDurations = segmentDurations;
    playback.cumulativeSegmentDurations = cumulativeSegmentDurations;
    playback.totalPlaybackMs = cumulativeDuration;
    if (playback.completed) {
      playback.elapsedPlaybackMs = cumulativeDuration;
      return;
    }
    if (playback.index <= 0) {
      playback.elapsedPlaybackMs = 0;
      return;
    }
    const previousSegmentIndex = Math.max(0, playback.index - 1);
    playback.elapsedPlaybackMs =
      cumulativeSegmentDurations[previousSegmentIndex] || 0;
    // Reset lastFrameTime so the next RAF frame treats this as a fresh start.
    // This prevents accumulated time from before the speed change from being
    // applied as a large delta on the next tick, and makes the speed change
    // feel immediate regardless of when in a segment it is triggered.
    if (playback.isPlaying) {
      playback.lastFrameTime = 0;
    }
  };

  buildPlaybackSegmentDurations = (
    pointTimestamps,
    pointCount,
    speedMultiplierRaw = null
  ) => {
    const segmentCount = Math.max(0, Number(pointCount || 0) - 1);
    if (segmentCount <= 0) return [];
    const fallback = Array(segmentCount).fill(
      this.trackPlaybackState.msPerStep
    );
    const times = Array.isArray(pointTimestamps) ? pointTimestamps : [];
    if (times.length !== pointCount) return fallback;
    const rawDurations = [];
    for (let index = 0; index < segmentCount; index += 1) {
      const startTime = Number(times[index]);
      const endTime = Number(times[index + 1]);
      if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) {
        return fallback;
      }
      rawDurations.push(Math.max(0, endTime - startTime));
    }
    const totalRawDuration = rawDurations.reduce(
      (sum, value) => sum + value,
      0
    );
    if (totalRawDuration <= 0) return fallback;
    const playbackSecondsPerRealMinute =
      Number(this.trackPlaybackState.playbackSecondsPerRealMinute) || 1;
    const multiplierFromArg = Number(speedMultiplierRaw);
    const multiplierFromState = Number(this.trackPlaybackState.speedMultiplier);
    const speedMultiplier =
      Number.isFinite(multiplierFromArg) && multiplierFromArg > 0
        ? multiplierFromArg
        : Number.isFinite(multiplierFromState) && multiplierFromState > 0
        ? multiplierFromState
        : 1;
    const effectiveSecondsPerRealMinute =
      playbackSecondsPerRealMinute / speedMultiplier;
    const playbackMsPerRealMs = effectiveSecondsPerRealMinute / 60;
    if (playbackMsPerRealMs <= 0) return fallback;
    // Cap is applied to the raw (real-world) segment duration — NOT the already-scaled
    // value. Applying the cap after scaling collapses 1×/2×/4× to the same per-segment
    // playback duration for tracks where GPS points are > ~10 s apart, making those
    // speed settings visually identical.
    //
    // By capping the raw duration first (using the base-speed scale to derive the limit)
    // the speed ratios are always preserved:
    //   maxRawSegmentMs at 1×  → maxStepDurationMs playback ms at every speed.
    const maxStepDurationMs = 3000;
    const baseMsPerRealMs = playbackSecondsPerRealMinute / 60; // scale factor at 1×
    const maxRawSegmentMs = maxStepDurationMs / baseMsPerRealMs; // e.g. 300 000 ms (5 min) at 1× default
    return rawDurations.map(
      value => Math.min(value, maxRawSegmentMs) * playbackMsPerRealMs
    );
  };

  collectTrackPlaybackTimeline = value => {
    const timeline = [];
    let timelineSource = value;
    const toValidCoordinate = coordinate => {
      if (!Array.isArray(coordinate) || coordinate.length < 2) return null;
      const lng = Number(coordinate[0]);
      const lat = Number(coordinate[1]);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
      return [lng, lat];
    };
    const normalizeDateTimeValue = rawValue => {
      const text = String(rawValue || '').trim();
      if (!text) return '';
      return text.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
    };
    const parseDateTimeValue = rawValue => {
      const normalized = normalizeDateTimeValue(rawValue);
      if (!normalized) return null;
      const timestamp = Date.parse(normalized);
      return Number.isFinite(timestamp) ? timestamp : null;
    };
    const areCoordinatesEqual = (left, right) => {
      if (
        !Array.isArray(left) ||
        left.length < 2 ||
        !Array.isArray(right) ||
        right.length < 2
      ) {
        return false;
      }
      const epsilon = 0.0000001;
      return (
        Math.abs(Number(left[0]) - Number(right[0])) <= epsilon &&
        Math.abs(Number(left[1]) - Number(right[1])) <= epsilon
      );
    };
    const pushPoint = (coordinate, timestamp = null) => {
      const validCoordinate = toValidCoordinate(coordinate);
      if (!validCoordinate) return;
      timeline.push({
        coordinate: validCoordinate,
        timestamp: Number.isFinite(Number(timestamp)) ? Number(timestamp) : null
      });
    };
    const resolveSourceEndpointHints = source => {
      if (!source || typeof source !== 'object') {
        return {
          startCoordinate: null,
          endCoordinate: null,
          startTimestamp: null,
          endTimestamp: null
        };
      }
      const properties = source.properties || {};
      const startCoordinate = toValidCoordinate(
        source.startWhere?.coordinates ||
          properties.startWhere?.coordinates ||
          properties.StartWhere?.coordinates
      );
      const endCoordinate = toValidCoordinate(
        source.endWhere?.coordinates ||
          properties.endWhere?.coordinates ||
          properties.EndWhere?.coordinates
      );
      return {
        startCoordinate,
        endCoordinate,
        startTimestamp: parseDateTimeValue(
          source.startWhen || properties.startWhen || properties.StartWhen
        ),
        endTimestamp: parseDateTimeValue(
          source.endWhen || properties.endWhen || properties.EndWhen
        )
      };
    };
    const dedupeSequentialPoints = source =>
      source.reduce((acc, point) => {
        if (!point || !Array.isArray(point.coordinate)) return acc;
        const previous = acc[acc.length - 1];
        if (
          !previous ||
          !areCoordinatesEqual(previous.coordinate, point.coordinate)
        ) {
          acc.push(point);
          return acc;
        }
        // Preserve timestamp when dedupe collapses identical consecutive points.
        if (previous.timestamp === null && point.timestamp !== null) {
          previous.timestamp = point.timestamp;
        }
        return acc;
      }, []);
    const extractGeometryDateTimes = source => {
      if (!source || typeof source !== 'object') return [];
      const properties = source.properties || {};
      const valueCandidates = [
        source.dateTimes,
        source.DateTimes,
        properties.dateTimes,
        properties.DateTimes,
        properties.timestamps,
        properties.Timestamps,
        properties.timestamp,
        properties.Timestamp
      ];
      for (let index = 0; index < valueCandidates.length; index += 1) {
        const candidate = valueCandidates[index];
        if (Array.isArray(candidate)) return candidate;
        if (typeof candidate === 'string' && candidate.trim())
          return [candidate];
      }
      return [];
    };
    const pushTimeOrderedGeometry = (geometry, source) => {
      if (
        !geometry ||
        !Array.isArray(geometry.coordinates) ||
        (geometry.type !== 'LineString' && geometry.type !== 'MultiPoint')
      ) {
        return false;
      }
      const dateTimes = extractGeometryDateTimes(source);
      if (
        !Array.isArray(dateTimes) ||
        dateTimes.length !== geometry.coordinates.length
      ) {
        return false;
      }
      const coordinateTimeline = geometry.coordinates
        .map((coordinate, index) => {
          const validCoordinate = toValidCoordinate(coordinate);
          const timestamp = parseDateTimeValue(dateTimes[index]);
          if (!validCoordinate || timestamp === null) return null;
          return {
            coordinate: validCoordinate,
            timestamp,
            index
          };
        })
        .filter(Boolean);
      if (coordinateTimeline.length < 2) return false;
      coordinateTimeline.sort((left, right) => {
        if (left.timestamp === right.timestamp) return left.index - right.index;
        return left.timestamp - right.timestamp;
      });
      coordinateTimeline.forEach(point =>
        pushPoint(point.coordinate, point.timestamp)
      );
      return true;
    };
    const pushGeometry = (geometry, source) => {
      if (!geometry || !geometry.type || !geometry.coordinates) return;
      if (geometry.type === 'Point') {
        pushPoint(geometry.coordinates);
        return;
      }
      if (geometry.type === 'LineString' || geometry.type === 'MultiPoint') {
        if (pushTimeOrderedGeometry(geometry, source)) return;
        geometry.coordinates.forEach(coordinate => pushPoint(coordinate));
        return;
      }
      if (geometry.type === 'MultiLineString') {
        geometry.coordinates.forEach(line => {
          if (Array.isArray(line)) {
            line.forEach(coordinate => pushPoint(coordinate));
          }
        });
      }
    };
    const toTimelineResult = source => {
      const deduped = dedupeSequentialPoints(source);
      let oriented = deduped;
      if (deduped.length >= 2) {
        const firstPoint = deduped[0];
        const lastPoint = deduped[deduped.length - 1];
        const {
          startCoordinate,
          endCoordinate,
          startTimestamp,
          endTimestamp
        } = resolveSourceEndpointHints(timelineSource);
        const hasPointTimestamps = deduped.every(point =>
          Number.isFinite(Number(point.timestamp))
        );
        if (
          startCoordinate &&
          endCoordinate &&
          areCoordinatesEqual(firstPoint.coordinate, endCoordinate) &&
          areCoordinatesEqual(lastPoint.coordinate, startCoordinate)
        ) {
          oriented = deduped.slice().reverse();
        } else if (
          hasPointTimestamps &&
          Number(firstPoint.timestamp) > Number(lastPoint.timestamp)
        ) {
          oriented = deduped.slice().reverse();
        } else if (
          startTimestamp !== null &&
          endTimestamp !== null &&
          startTimestamp > endTimestamp
        ) {
          oriented = deduped.slice().reverse();
        }
      }
      const coordinates = oriented.map(point => point.coordinate);
      const hasFullTimestampCoverage =
        oriented.length > 1 &&
        oriented.every(point => Number.isFinite(Number(point.timestamp)));
      const pointTimestamps = hasFullTimestampCoverage
        ? oriented.map(point => Number(point.timestamp))
        : [];
      return {
        coordinates,
        pointTimestamps
      };
    };

    if (value && value.geometry) {
      pushGeometry(value.geometry, value);
    }
    if (timeline.length >= 2) {
      return toTimelineResult(timeline);
    }

    const startCoordinate =
      value && value.startWhere && Array.isArray(value.startWhere.coordinates)
        ? toValidCoordinate(value.startWhere.coordinates)
        : null;
    const endCoordinate =
      value && value.endWhere && Array.isArray(value.endWhere.coordinates)
        ? toValidCoordinate(value.endWhere.coordinates)
        : null;
    const startTimestamp = parseDateTimeValue(value && value.startWhen);
    const endTimestamp = parseDateTimeValue(value && value.endWhen);
    if (startCoordinate) {
      pushPoint(startCoordinate, startTimestamp);
    }
    if (
      endCoordinate &&
      (!startCoordinate || !areCoordinatesEqual(startCoordinate, endCoordinate))
    ) {
      pushPoint(endCoordinate, endTimestamp);
    }
    if (timeline.length >= 2) {
      return toTimelineResult(timeline);
    }

    const requestedTrackId = String(
      (value && (value.trackId || value.featureId || value.EntityId)) || ''
    ).trim();
    if (!requestedTrackId) {
      return toTimelineResult(timeline);
    }
    const trackLayerFeatures = Array.isArray(this.props.trackLayer?.features)
      ? this.props.trackLayer.features
      : [];
    const matchingFeature = trackLayerFeatures.find(feature =>
      this.trackIdsMatch(this.getTrackFeatureId(feature), requestedTrackId)
    );
    if (matchingFeature) {
      timeline.length = 0;
      timelineSource = matchingFeature;
      pushGeometry(matchingFeature.geometry, matchingFeature);
    }
    return toTimelineResult(timeline);
  };

  pauseTrackPlayback = () => {
    const playback = this.trackPlaybackState;
    if (!playback.isPlaying || !playback.trackId) return;
    playback.isPlaying = false;
    this.clearTrackPlaybackFrame();
    this.emitTrackPlaybackStatus('paused', playback.trackId);
  };

  stopTrackPlayback = (status, syncState = true, clearToasts = true) => {
    const playback = this.trackPlaybackState;
    const previousTrackId = playback.trackId;
    this.clearTrackPlaybackFrame();
    playback.trackId = null;
    playback.coordinates = [];
    playback.pointTimestamps = [];
    playback.segmentDurations = [];
    playback.cumulativeSegmentDurations = [];
    playback.cumulativeSegmentDistances = [];
    playback.elapsedPlaybackMs = 0;
    playback.totalPlaybackMs = 0;
    playback.index = 0;
    playback.completed = false;
    playback.isPlaying = false;
    playback.lastFrameTime = 0;
    playback.observationEvents = [];
    playback.firedObservationIds = new Set();
    this.setTrackPlaybackPoint(null);
    this.setTrackPlaybackProgress([], 0);
    this.setTrackPlaybackObservationHighlight(null);
    if (this.playbackObservationHighlightTimer) {
      clearTimeout(this.playbackObservationHighlightTimer);
      this.playbackObservationHighlightTimer = null;
    }
    if (clearToasts) {
      this.playbackObservationToastTimers.forEach(timerId =>
        clearTimeout(timerId)
      );
      this.playbackObservationToastTimers.clear();
    }
    if (syncState && clearToasts) {
      this.setState({ playbackObservationToasts: [] });
    }
    if (status) {
      this.emitTrackPlaybackStatus(status, previousTrackId);
    }
  };

  runTrackPlaybackFrame = timestamp => {
    const playback = this.trackPlaybackState;
    if (!playback.isPlaying || !playback.trackId) return;
    if (!playback.lastFrameTime) {
      playback.lastFrameTime = timestamp;
    }
    const frameDelta = Math.max(0, timestamp - playback.lastFrameTime);
    playback.lastFrameTime = timestamp;
    playback.elapsedPlaybackMs = Math.min(
      playback.totalPlaybackMs,
      playback.elapsedPlaybackMs + frameDelta
    );

    const cumulativeDurations = Array.isArray(
      playback.cumulativeSegmentDurations
    )
      ? playback.cumulativeSegmentDurations
      : [];
    let nextIndex = playback.index;
    while (
      nextIndex < cumulativeDurations.length &&
      playback.elapsedPlaybackMs >= cumulativeDurations[nextIndex]
    ) {
      nextIndex += 1;
    }
    if (nextIndex !== playback.index) {
      playback.index = Math.min(nextIndex, playback.coordinates.length - 1);
      this.setTrackPlaybackPoint(playback.coordinates[playback.index]);
      this.setTrackPlaybackProgress(playback.coordinates, playback.index);
      this.triggerPlaybackObservationEventsUpToIndex(playback.index);
      this.syncPlaybackTrackInfoForIndex(playback, playback.index);
      if (playback.index >= playback.coordinates.length - 1) {
        this.clearTrackPlaybackFrame();
        playback.isPlaying = false;
        playback.lastFrameTime = 0;
        playback.completed = true;
        this.emitTrackPlaybackStatus('completed', playback.trackId);
        return;
      }
    }
    playback.rafId = window.requestAnimationFrame(this.runTrackPlaybackFrame);
  };

  startTrackPlayback = async detail => {
    if (!this.map || !this.map.getSource(this.trackPlaybackSourceId)) {
      this.emitTrackPlaybackStatus('stopped', detail && detail.trackId);
      return;
    }
    const trackId = String(detail && detail.trackId).trim();
    const trackItem = detail && detail.trackItem;
    const commandSpeedMultiplier = Number(detail && detail.speedMultiplier);
    const playback = this.trackPlaybackState;
    if (!trackId) {
      this.emitTrackPlaybackStatus('stopped', null);
      return;
    }

    if (playback.trackId === trackId && playback.coordinates.length >= 2) {
      if (playback.isPlaying) return;
      if (
        Number.isFinite(commandSpeedMultiplier) &&
        commandSpeedMultiplier > 0 &&
        commandSpeedMultiplier !== playback.speedMultiplier
      ) {
        this.setTrackPlaybackSpeed(commandSpeedMultiplier);
      }
      const isAtOrPastEnd =
        playback.index >= Math.max(0, playback.coordinates.length - 1);
      if (playback.completed || isAtOrPastEnd) {
        playback.index = 0;
        playback.elapsedPlaybackMs = 0;
        playback.lastFrameTime = 0;
        playback.completed = false;
        playback.firedObservationIds = new Set();
        this.setTrackPlaybackPoint(playback.coordinates[0]);
        this.setTrackPlaybackProgress(playback.coordinates, 0);
        this.syncPlaybackTrackInfoForIndex(playback, 0);
      }
      playback.isPlaying = true;
      playback.lastFrameTime = 0;
      this.emitTrackPlaybackStatus('playing', trackId);
      this.syncPlaybackTrackInfoForIndex(playback, playback.index);
      playback.rafId = window.requestAnimationFrame(this.runTrackPlaybackFrame);
      return;
    }

    let timeline = this.collectTrackPlaybackTimeline(trackItem);
    let coordinates = timeline.coordinates;
    if (
      (coordinates.length < 2 || timeline.pointTimestamps.length === 0) &&
      trackId
    ) {
      // Ensure full GPS trace + timestamps are loaded before playback.
      // Tracks from the map path only have startWhere/endWhere (2 coords) and
      // no dateTimes, so coordinates.length < 2 alone is not sufficient —
      // we must also check for missing timestamps.
      if (this.trackPlaybackPreparingTrackId !== trackId) {
        this.trackPlaybackPreparingTrackId = trackId;
        try {
          await this.getEntireTrack(trackId);
        } catch (error) {
          // Ignore load failures here and fall through to the guarded stop.
        } finally {
          this.trackPlaybackPreparingTrackId = null;
        }
      }
      // After getEntireTrack/getTrackInfo the Redux store has been updated;
      // find the fresh timeline item so collectTrackPlaybackTimeline picks up
      // the full geometry and dateTimes.
      const normalizedTrackId = String(trackId || '').trim();
      const timelineResultsList = Array.isArray(this.props.timelineResults)
        ? this.props.timelineResults
        : [];
      const freshTimelineItem = timelineResultsList.find(result => {
        const content = Array.isArray(result?.extracted?.content)
          ? result.extracted.content
          : [];
        const geoFeatureEntry = content.find(
          entry => entry && entry.GeoFeature
        );
        const geoFeature = geoFeatureEntry && geoFeatureEntry.GeoFeature;
        if (!geoFeature) return false;
        const props = geoFeature.properties || {};
        return [
          geoFeature.featureId,
          geoFeature.trackId,
          geoFeature.EntityId,
          props.EntityId,
          props.featureId,
          props.trackId
        ]
          .map(v => String(v || '').trim())
          .filter(Boolean)
          .includes(normalizedTrackId);
      });
      timeline = this.collectTrackPlaybackTimeline(
        freshTimelineItem || trackItem
      );
      coordinates = timeline.coordinates;
    }
    if (coordinates.length < 2) {
      this.stopTrackPlayback('stopped');
      return;
    }

    const pointTimestamps = timeline.pointTimestamps;
    const selectedSpeedMultiplier =
      Number.isFinite(commandSpeedMultiplier) && commandSpeedMultiplier > 0
        ? commandSpeedMultiplier
        : Number(this.trackPlaybackState.speedMultiplier) > 0
        ? Number(this.trackPlaybackState.speedMultiplier)
        : 1;
    playback.speedMultiplier = selectedSpeedMultiplier;
    const segmentDurations = this.buildPlaybackSegmentDurations(
      pointTimestamps,
      coordinates.length,
      selectedSpeedMultiplier
    );
    const cumulativeSegmentDurations = [];
    let cumulativeDuration = 0;
    segmentDurations.forEach(value => {
      cumulativeDuration += Number(value) || 0;
      cumulativeSegmentDurations.push(cumulativeDuration);
    });

    this.clearTrackPlaybackFrame();
    playback.trackId = trackId;
    playback.coordinates = coordinates;
    playback.pointTimestamps = pointTimestamps;
    playback.segmentDurations = segmentDurations;
    playback.cumulativeSegmentDurations = cumulativeSegmentDurations;
    playback.cumulativeSegmentDistances = this.buildCumulativeSegmentDistances(
      coordinates
    );
    playback.elapsedPlaybackMs = 0;
    playback.totalPlaybackMs = cumulativeDuration;
    playback.index = 0;
    playback.completed = false;
    playback.isPlaying = true;
    playback.lastFrameTime = 0;
    playback.observationEvents = this.getObservationPlaybackEvents(
      trackId,
      coordinates,
      pointTimestamps
    );
    playback.firedObservationIds = new Set();
    this.setTrackPlaybackPoint(coordinates[0]);
    this.setTrackPlaybackProgress(coordinates, 0);
    this.zoomToPlaybackBounds(coordinates);
    this.syncPlaybackTrackInfoForIndex(playback, 0);
    // Ensure start/end endpoints are shown by making this the selected item,
    // mirroring what happens when a track is selected from the results list.
    if (trackItem && this.props.setSelectedItem) {
      this.props.setSelectedItem(trackItem);
    }
    this.emitTrackPlaybackStatus('playing', trackId);
    playback.rafId = window.requestAnimationFrame(this.runTrackPlaybackFrame);
  };

  // Update layers with new data
  updateMapLayers = () => {
    if (this.map) {
      const dirtyLayers = this._dirtyLayersSinceLastUpdate;
      this._dirtyLayersSinceLastUpdate = null;

      const obsIsDirty = !dirtyLayers || dirtyLayers.has('Observations');
      if (obsIsDirty) {
        this.clearObservationSpider();
        this.overlapObservationGroups = {};
      }

      const selectedAgentValues = this.getSelectedAgentMatchValues();
      const trackFocusedObservationIds = this.getTrackFocusedObservationIds();
      this.props.mapLayers.forEach(layer => {
        if (dirtyLayers && !dirtyLayers.has(layer.name)) return;
        if (layer.features) {
          if (this.map.getLayer(layer.name)) {
            const filteredFeatures = layer.features
              .filter(item => {
                const props = item.properties || {};
                if (
                  (layer.name === 'Observations' ||
                    layer.name === 'Tracks' ||
                    layer.name === 'Agents') &&
                  !this.featureMatchesSelectedAgents(item, selectedAgentValues)
                ) {
                  return false;
                }
                if (layer.name === 'Agents') {
                  if (!this.props.hideOldAgents) return true;
                  const age = props.age || '';
                  const selectedId =
                    this.props.selectedItem &&
                    this.props.selectedItem.properties &&
                    this.props.selectedItem.properties.EntityId;
                  const isSelected =
                    selectedId && props.EntityId === selectedId;
                  const isOldByAge = age.startsWith('moreThanWeek');
                  const lastSeen = props.LastSeenDateTime;
                  const isOldByDate = lastSeen
                    ? moment().diff(moment(lastSeen), 'days') > 7
                    : false;
                  const isOld = isOldByAge || isOldByDate;
                  return isSelected || !isOld;
                }

                if (layer.name === 'Observations') {
                  if (
                    trackFocusedObservationIds &&
                    trackFocusedObservationIds.size > 0
                  ) {
                    const featureObservationId = this.getObservationFeatureId(
                      item
                    );
                    return (
                      !!featureObservationId &&
                      trackFocusedObservationIds.has(featureObservationId)
                    );
                  }
                  return true;
                }

                const lastSeen = props.LastSeenDateTime;
                if (!lastSeen) return true;
                return moment().diff(moment(lastSeen), 'days') < 7;
              })
              .map(item => {
                delete item.DocId;
                item.properties.observation = 'observation';
                return item;
              });

            // Agent labels (registration name, else agent name, else
            // "Old sensor agent N", else the raw id) are resolved once in the
            // layer reducer and read from properties.DisplayLabel. They used to
            // be re-derived here from EntityId, which is why the map and the
            // filter panel could disagree on both the label and its number.

            if (layer.name === 'Observations') {
              const normalized = filteredFeatures.map(
                this.normalizeObservationAggregateFeature
              );
              const backendClusters = normalized.filter(
                f =>
                  f.properties &&
                  f.properties.EntityType === 'cluster' &&
                  !f.properties.overlap_cluster
              );
              const individuals = normalized.filter(
                f => !f.properties || f.properties.EntityType !== 'cluster'
              );

              // Show data immediately so Mapbox can start rendering without
              // waiting for the (potentially expensive) overlap-cluster pass.
              this.map.getSource(layer.name).setData({
                type: layer.type,
                features: [...individuals, ...backendClusters]
              });
              this.setObservationMarkerLayerVisibility(
                this.props.selectedLayers.includes('Observations')
                  ? 'visible'
                  : 'none'
              );

              // Cancel any previous pending overlap computation for stale data.
              if (this._pendingOverlapCallbackId !== null) {
                if ('cancelIdleCallback' in window) {
                  cancelIdleCallback(this._pendingOverlapCallbackId);
                }
                this._pendingOverlapCallbackId = null;
              }

              // Defer the overlap-cluster grouping so it doesn't block the
              // paint that makes the initial data visible.
              const applyOverlapClusters = () => {
                this._pendingOverlapCallbackId = null;
                if (!this.map || !this.map.getSource(layer.name)) return;
                const processed = this.buildObservationDataWithOverlapClusters(
                  individuals
                );
                this.map.getSource(layer.name).setData({
                  type: layer.type,
                  features: [...processed, ...backendClusters]
                });
              };

              if ('requestIdleCallback' in window) {
                this._pendingOverlapCallbackId = requestIdleCallback(
                  applyOverlapClusters,
                  { timeout: 1000 }
                );
              } else {
                setTimeout(applyOverlapClusters, 0);
              }
            } else {
              let data = {
                type: layer.type,
                features: filteredFeatures
              };

              this.map.getSource(layer.name).setData(data);
            }
          } else if (layer.projectId !== 'default') {
            this.setGeoJsonLayer(layer);
          }
        }
      });

      if (this.map.getSource('alert') && this.props.alertFeatures) {
        this.map.getSource('alert').setData({
          type: 'FeatureCollection',
          features: this.props.alertFeatures.map(item => {
            item.properties.observation = 'observation';
            return item;
          })
        });
      }

      if (
        this.map.getSource('actualTracks') ||
        this.map.getSource(this.trackEndpointsSourceId)
      ) {
        this.updateActualTracksLayers();
      }

      // After any marker refresh, keep heatmap-mode layers consistent so a
      // data refresh can't resurrect markers under an active heatmap.
      Object.keys(HEATMAP_LAYER_CONFIG).forEach(name =>
        this.applyHeatmapVisibility(name)
      );
    }
  };

  updateActualTracksLayers = () => {
    actualTracksHandlers.updateActualTracksLayers.call(this);
    this.enforceCenterpointsTrackLimit();
  };

  // 'Track centerpoints' (the default mode) renders one dot per GPS point
  // across every visible track, rebuilt from scratch on every refresh (see
  // MAX_CENTERPOINT_TRACKS in actualTracks.js for why that's a rendering
  // cost concern on top of the usual readability ceiling). Past that many
  // simultaneously visible tracks, fall back to 'Track heatmap'
  // automatically and tell the user why. Only fires while centerpoints is
  // actually the active display mode, so a background data refresh while
  // some other mode is showing can't force-switch things out from under
  // the user.
  enforceCenterpointsTrackLimit = () => {
    if (this.getTrackDisplayMode() !== 'centerpoints') return;
    const trackCount = this.latestActualTracksTrackCount || 0;
    if (trackCount <= actualTracksHandlers.MAX_CENTERPOINT_TRACKS) return;

    this.setTrackDisplayMode('heatmap');
    this.setState({ centerpointsFallbackNotice: { trackCount } });
  };

  dismissCenterpointsFallbackNotice = () => {
    this.setState({ centerpointsFallbackNotice: null });
  };

  getPulsingDot = (color, size, duration, localMap) => {
    let dot = {
      width: size,
      height: size,
      data: new Uint8Array(size * size * 4),

      // get rendering context for the map canvas when layer is added to the map
      onAdd: function() {
        var canvas = document.createElement('canvas');
        canvas.width = this.width;
        canvas.height = this.height;
        this.context = canvas.getContext('2d');
      },

      // called once before every frame where the icon will be used
      render: function() {
        // var duration = 5000;
        var t = (performance.now() % duration) / duration;

        var radius = (size / 2) * 0.3;
        var outerRadius = (size / 2) * 0.7 * t + radius;
        var context = this.context;

        // draw outer circle
        context.clearRect(0, 0, this.width, this.height);
        context.beginPath();
        context.arc(
          this.width / 2,
          this.height / 2,
          outerRadius,
          0,
          Math.PI * 2
        );
        context.fillStyle = 'rgba(' + color + ',' + (1 - t) + ')';
        context.fill();

        // draw inner circle
        context.beginPath();
        context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
        context.fillStyle = 'rgba(' + color + ', 1)';
        context.strokeStyle = '#000';
        context.lineWidth = 4 + 8 * (1 - t);
        context.fill();
        context.stroke();

        // update this image's data with data from the canvas
        this.data = context.getImageData(0, 0, this.width, this.height).data;

        // continuously repaint the map, resulting in the smooth animation of the dot
        localMap.triggerRepaint();

        // return `true` to let the map know that the image was updated
        return true;
      }
    };

    return dot;
  };

  getPulsingPin = (color, size, duration, localMap) => {
    return this.getPulsingDot(color, size, duration, localMap);
  };

  getStaticDot = (color, size, localMap) => {
    let dot = {
      width: size,
      height: size,
      data: new Uint8Array(size * size * 4),

      onAdd: function() {
        var canvas = document.createElement('canvas');
        canvas.width = this.width;
        canvas.height = this.height;
        this.context = canvas.getContext('2d');
      },

      render: function() {
        var context = this.context;
        var radius = (size / 2) * 0.3;
        context.clearRect(0, 0, this.width, this.height);
        context.beginPath();
        context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
        context.fillStyle = 'rgba(' + color + ', 1)';
        context.strokeStyle = 'white';
        context.lineWidth = 4;
        context.fill();
        context.stroke();
        this.data = context.getImageData(0, 0, this.width, this.height).data;
        return true;
      }
    };

    return dot;
  };

  getStaticPin = (color, size, localMap) => {
    return this.getStaticDot(color, size, localMap);
  };

  getClusterLayerIds = sourceName =>
    clustersHandlers.getClusterLayerIds.call(this, sourceName);

  getObservationPixelBucketSize = () =>
    observationClustersHandlers.getObservationPixelBucketSize.call(this);

  addBackendObservationClusterLayers = visibility =>
    observationClustersHandlers.addBackendObservationClusterLayers.call(
      this,
      visibility
    );

  addCamtrapClusterLayer = visibility =>
    observationClustersHandlers.addCamtrapClusterLayer.call(this, visibility);

  rebuildObservationSourceAndLayers = () =>
    observationClustersHandlers.rebuildObservationSourceAndLayers.call(this);

  setObservationMarkerLayerVisibility = visibility =>
    observationClustersHandlers.setObservationMarkerLayerVisibility.call(
      this,
      visibility
    );

  ensureClusterSelectionIndicator = () =>
    observationClustersHandlers.ensureClusterSelectionIndicator.call(this);

  setClusterSelectionFilter = (overlapKey, clusterFeature = null) =>
    observationClustersHandlers.setClusterSelectionFilter.call(
      this,
      overlapKey,
      clusterFeature
    );

  isBackendClusterFeature = feature =>
    observationClustersHandlers.isBackendClusterFeature.call(this, feature);

  getObservationFeatureCount = feature =>
    observationClustersHandlers.getObservationFeatureCount.call(this, feature);

  normalizeObservationAggregateFeature = feature =>
    observationClustersHandlers.normalizeObservationAggregateFeature.call(
      this,
      feature
    );

  getObservationOverlapKey = coordinates =>
    observationClustersHandlers.getObservationOverlapKey.call(
      this,
      coordinates
    );

  buildObservationDataWithOverlapClusters = features =>
    observationClustersHandlers.buildObservationDataWithOverlapClusters.call(
      this,
      features
    );

  clearObservationSpider = () =>
    spiderHandlers.clearObservationSpider.call(this);

  getClusterLeaves = (sourceName, clusterId, limit = 1000) =>
    spiderHandlers.getClusterLeaves.call(this, sourceName, clusterId, limit);

  tryOpenSpiderForSameCoordinateCluster = async (sourceName, clusterFeature) =>
    spiderHandlers.tryOpenSpiderForSameCoordinateCluster.call(
      this,
      sourceName,
      clusterFeature
    );

  setClusterLayerVisibility = (sourceName, visibility) =>
    clustersHandlers.setClusterLayerVisibility.call(
      this,
      sourceName,
      visibility
    );

  addHeatmapLayer = (layerName, visibility = 'none', fillColor, opacity) =>
    heatmapHandlers.addHeatmapLayer.call(
      this,
      layerName,
      visibility,
      fillColor,
      opacity
    );

  setHeatmapOpacity = (layerName, opacity) =>
    heatmapHandlers.setHeatmapOpacity.call(this, layerName, opacity);

  updateHeatmapSource = (layerName, geojson) =>
    heatmapHandlers.updateHeatmapSource.call(this, layerName, geojson);

  setHeatmapVisibility = (layerName, visibility) =>
    heatmapHandlers.setHeatmapVisibility.call(this, layerName, visibility);

  addTracksFullStandardLayer = (visibility = 'none') =>
    tracksFullStandardHandlers.addTracksFullStandardLayer.call(
      this,
      visibility
    );

  updateTracksFullStandardLayer = () =>
    tracksFullStandardHandlers.updateTracksFullStandardLayer.call(this);

  addTracksFullColourLayer = (visibility = 'none') =>
    tracksFullColourHandlers.addTracksFullColourLayer.call(this, visibility);

  // Re-applies the current hour-range filter every time the source data
  // rebuilds (pan/refresh/mode switch), not just when the slider itself
  // moves - a freshly (re)created layer has no filter of its own yet.
  updateTracksFullColourLayer = () => {
    tracksFullColourHandlers.updateTracksFullColourLayer.call(this);
    this.applyTrackHourRangeFilter();
    this.enforceColourCodedTrackLimit();
  };

  // Colour-coded mode stops being readable well before rendering actually
  // struggles (see MAX_COLOUR_CODED_TRACKS in tracksFullColour.js for why
  // there's no single "correct" number) - past that many simultaneously
  // active tracks, fall back to 'Full tracks - standard' automatically and
  // tell the user why, rather than leaving them looking at an
  // uninterpretable smear with no explanation. Only fires while colour
  // mode is actually the active display mode, so it doesn't re-trigger on
  // every rebuild once already switched away.
  enforceColourCodedTrackLimit = () => {
    if (!this.props.showTrackFullColour) return;
    const trackCount = this.getActiveFullTraceTrackIds().size;
    if (trackCount <= tracksFullColourHandlers.MAX_COLOUR_CODED_TRACKS) return;

    this.setTrackDisplayMode('full');
    this.setState({ colourModeFallbackNotice: { trackCount } });
  };

  dismissColourModeFallbackNotice = () => {
    this.setState({ colourModeFallbackNotice: null });
  };

  // Hides "Full tracks - colour coded" segments whose hour-of-day falls
  // outside the user's selected range (see the dual-handle slider on the
  // sky-gradient legend). Segments without timing data (properties.hour
  // undefined - see tracksFullColour.toColouredSegments' untimed fallback)
  // always show, since the concept of "hour" doesn't apply to them. A pure
  // GL filter, not a data rebuild, so it's instant regardless of how many
  // segments are loaded.
  applyTrackHourRangeFilter = () => {
    if (!this.map) return;
    const start = Number(this.state.trackHourRangeStart);
    const end = Number(this.state.trackHourRangeEnd);
    const isFullRange =
      !Number.isFinite(start) ||
      !Number.isFinite(end) ||
      (start <= 0 && end >= 24);
    const hourFilter = isFullRange
      ? null
      : [
          'any',
          ['!', ['has', 'hour']],
          ['all', ['>=', ['get', 'hour'], start], ['<=', ['get', 'hour'], end]]
        ];
    if (!this._trackLayerBaselineFilters) this._trackLayerBaselineFilters = {};
    [
      tracksFullColourHandlers.TRACKS_FULL_COLOUR_LAYER_ID,
      tracksFullColourHandlers.TRACKS_FULL_COLOUR_OUTLINE_LAYER_ID,
      // Single selected/played-back track (see actualTracks.js
      // isColourCoded) shares the same per-segment 'hour' property, so the
      // same slider/filter applies here too rather than needing its own -
      // 'actualTracks_points' is the per-GPS-fix dot layer sharing the same
      // source (see layers.js setActualTrackLayer), also tagged with 'hour'
      // per dot (see actualTracks.js addTracePoint), so it needs the same
      // treatment or its dots (each with their own black stroke) keep
      // showing regardless of the slider - densely spaced, that reads as a
      // solid black line replacing the "hidden" line rather than a gap.
      'actualTracks',
      'actualTracks_outline',
      'actualTracks_points'
    ].forEach(layerId => {
      if (!this.map.getLayer(layerId)) return;
      // setFilter() replaces a layer's filter outright rather than merging
      // with it, so naively applying hourFilter here would also wipe out
      // each layer's own baseline filter (e.g. the geometry-type check on
      // the line layers, or the geometry-type + _tracePoint check on the
      // dots layer - see layers.js) - cache it once per layer, read
      // straight from the map rather than redefining it here, and combine
      // the two with 'all' instead of overwriting.
      if (!(layerId in this._trackLayerBaselineFilters)) {
        this._trackLayerBaselineFilters[layerId] =
          this.map.getFilter(layerId) || null;
      }
      const baseline = this._trackLayerBaselineFilters[layerId];
      const combinedFilter = !hourFilter
        ? baseline
        : baseline
        ? ['all', baseline, hourFilter]
        : hourFilter;
      this.map.setFilter(layerId, combinedFilter);
    });
  };

  // Single source of truth for whether the currently visible single track
  // is being rendered colour-coded by hour of day - see actualTracks.js'
  // isColourCoded (trackColor === 'colourCoded' preference + exactly one
  // visible track). Called from updateActualTracksLayers on every rebuild;
  // guarded so it only re-renders when the value actually changes.
  setSingleTrackColourCoded = isColourCoded => {
    this.setState(prevState => {
      if (prevState.singleTrackColourCoded === isColourCoded) return null;
      return {
        singleTrackColourCoded: isColourCoded,
        ...(isColourCoded ? { singleTrackColourLegendDismissed: false } : {})
      };
    });
  };

  // Slider handler for the hour-range legend control. `which` is 'start' or
  // 'end'; values are clamped so the two handles can never cross.
  changeTrackHourRange = (which, rawValue) => {
    const value = Math.max(0, Math.min(24, Number(rawValue)));
    if (!Number.isFinite(value)) return;
    this.setState(prevState => {
      const nextStart =
        which === 'start'
          ? Math.min(value, prevState.trackHourRangeEnd)
          : prevState.trackHourRangeStart;
      const nextEnd =
        which === 'end'
          ? Math.max(value, prevState.trackHourRangeStart)
          : prevState.trackHourRangeEnd;
      return { trackHourRangeStart: nextStart, trackHourRangeEnd: nextEnd };
    }, this.applyTrackHourRangeFilter);
  };

  // Begins a drag on one of the two hour-range handles. Listens on
  // `document` rather than the handle itself so the drag keeps tracking
  // even when the pointer moves faster than the handle can follow.
  startTrackHourDrag = which => e => {
    e.preventDefault();
    this.setState({ draggingTrackHourHandle: which });
    document.addEventListener('pointermove', this.onTrackHourDrag);
    document.addEventListener('pointerup', this.endTrackHourDrag);
  };

  onTrackHourDrag = e => {
    const { draggingTrackHourHandle } = this.state;
    if (!draggingTrackHourHandle || !this.trackHourRangeBarEl) return;
    const rect = this.trackHourRangeBarEl.getBoundingClientRect();
    const ratio =
      rect.width > 0
        ? Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
        : 0;
    this.changeTrackHourRange(draggingTrackHourHandle, Math.round(ratio * 24));
  };

  endTrackHourDrag = () => {
    document.removeEventListener('pointermove', this.onTrackHourDrag);
    document.removeEventListener('pointerup', this.endTrackHourDrag);
    this.setState({ draggingTrackHourHandle: null });
  };

  // Arrow-key adjustment - this is a plain positioned <div role="slider">,
  // not a native <input type="range">, so keyboard support needs wiring up
  // by hand to keep the control accessible.
  handleTrackHourKeyDown = (which, e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const current =
      which === 'start'
        ? this.state.trackHourRangeStart
        : this.state.trackHourRangeEnd;
    this.changeTrackHourRange(
      which,
      current + (e.key === 'ArrowLeft' ? -1 : 1)
    );
  };

  // Shows/hides the normal marker (and, for Observations, cluster) layer for
  // a given layer name - used to swap it out while its heatmap is active.
  setLayerMarkersVisibility = (layerName, visibility) => {
    if (layerName === 'Observations') {
      this.setObservationMarkerLayerVisibility(visibility);
      return;
    }
    if (layerName === 'Tracks') {
      // Centerpoint icons only show in 'centerpoints' mode; 'full' and
      // 'fullColour' modes swap them for their respective line layers (see
      // applyTrackFullStandardVisibility / applyTrackFullColourVisibility).
      // Heatmap mode hides all of them.
      const iconVisibility =
        visibility === 'visible' &&
        !this.props.showTrackFullStandard &&
        !this.props.showTrackFullColour
          ? 'visible'
          : 'none';
      if (this.map && this.map.getLayer('Tracks')) {
        this.map.setLayoutProperty('Tracks', 'visibility', iconVisibility);
      }
      if (this.map && this.map.getLayer('Tracks_clicked')) {
        this.map.setLayoutProperty(
          'Tracks_clicked',
          'visibility',
          iconVisibility
        );
      }
      this.applyTrackFullStandardVisibility();
      this.applyTrackFullColourVisibility();
      return;
    }
    if (this.map && this.map.getLayer(layerName)) {
      this.map.setLayoutProperty(layerName, 'visibility', visibility);
    }
  };

  // Single source of truth for the plain "Full tracks - standard" line
  // layer: visible only when Tracks is selected, not in heatmap mode, and
  // 'full' is the active display mode. Called both from
  // setLayerMarkersVisibility (mode/selection changes) and from
  // componentDidUpdate (direct showTrackFullStandard changes).
  applyTrackFullStandardVisibility = () => {
    if (!this.map || !this.map.getLayer('Tracks_lines')) return;
    const isSelected = this.props.selectedLayers.includes('Tracks');
    const visible =
      isSelected &&
      !this.props.showTrackHeatmap &&
      this.props.showTrackFullStandard;
    this.map.setLayoutProperty(
      'Tracks_lines',
      'visibility',
      visible ? 'visible' : 'none'
    );
  };

  // Same single-source-of-truth pattern as applyTrackFullStandardVisibility,
  // for the colour-coded line + outline pair.
  applyTrackFullColourVisibility = () => {
    if (!this.map || !this.map.getLayer('Tracks_lines_colour')) return;
    const isSelected = this.props.selectedLayers.includes('Tracks');
    const visible =
      isSelected &&
      !this.props.showTrackHeatmap &&
      this.props.showTrackFullColour;
    this.map.setLayoutProperty(
      'Tracks_lines_colour',
      'visibility',
      visible ? 'visible' : 'none'
    );
    if (this.map.getLayer('Tracks_lines_colour_outline')) {
      this.map.setLayoutProperty(
        'Tracks_lines_colour_outline',
        'visibility',
        visible ? 'visible' : 'none'
      );
    }
  };

  // The bulk Tracks fetch (this.props.mapLayers -> the 'Tracks' entry's
  // .features) is what actually feeds the map with every track currently in
  // view - NOT this.props.trackLayer, which only ever holds a single
  // individually-selected track's data (see store/layer SET_ENTIRE_TRACK).
  getTracksMapLayerFeatures = () => {
    // Match the same 'default project' + name check used everywhere else
    // (isDefaultTracksLayer in sources.js) - a plain name match can pick up
    // a different, feature-less 'Tracks' entry if more than one exists.
    const tracksMapLayer = (this.props.mapLayers || []).find(
      layer =>
        layer &&
        layer.name === 'Tracks' &&
        String(layer.projectId) === 'default'
    );
    return Array.isArray(tracksMapLayer && tracksMapLayer.features)
      ? tracksMapLayer.features
      : [];
  };

  // Both "Full tracks - standard" and "Full tracks - colour coded" need to
  // answer "which track ids should currently have a rendered line" from the
  // fullTrackTraces cache. Naively using "every id ever cached" never prunes
  // anything - a track's line stays on the map forever once fetched, even
  // long after panning away, until a full page reload. But naively using
  // "only whatever's in the current stub list this instant" reintroduces
  // the flashing bug fixed earlier (a single bulk Tracks fetch can
  // transiently omit a track that's still genuinely on screen at ~90+
  // tracks). Split the difference: a track counts as active if it's in the
  // current stub list, or was as recently as TRACK_TRACE_ACTIVE_GRACE_MS
  // ago - long enough to absorb one flaky fetch cycle, short enough that
  // panning away actually stops rendering it soon after.
  //
  // Single shared source of truth (per Dr J's "one location" preference) -
  // both tracksFullStandard.js and tracksFullColour.js call this instead of
  // each computing their own union.
  getActiveFullTraceTrackIds = () => {
    const TRACK_TRACE_ACTIVE_GRACE_MS = 15000;
    if (!this._trackLastSeenAt) this._trackLastSeenAt = new Map();

    const stubFeatures = this.getTracksMapLayerFeatures();

    // A genuinely empty stub list (the bulk Tracks fetch returned zero
    // features - e.g. the selected period no longer has any matching
    // tracks) is a different signal than "one track missing from a list of
    // 90+": that's the flaky-omission case the grace period exists to
    // absorb, but "every single track vanished at once" isn't a flaky
    // partial fetch, it's the filters genuinely changing. Clear immediately
    // rather than leaving stale tracks on the map for the rest of the
    // grace window (or indefinitely, if nothing else happens to re-trigger
    // this afterwards).
    if (stubFeatures.length === 0) {
      this._trackLastSeenAt.clear();
      return new Set();
    }

    const now = Date.now();
    stubFeatures.forEach(feature => {
      const trackId = this.getTrackFeatureId(feature);
      if (trackId) this._trackLastSeenAt.set(trackId, now);
    });

    const activeIds = new Set();
    this._trackLastSeenAt.forEach((lastSeenAt, trackId) => {
      if (now - lastSeenAt <= TRACK_TRACE_ACTIVE_GRACE_MS) {
        activeIds.add(trackId);
      } else {
        this._trackLastSeenAt.delete(trackId);
      }
    });
    return activeIds;
  };

  // Generic "how many features does this default-project layer currently
  // have" count - used for the Full tracks info panel (Agents/Tracks
  // counts). Same projectId-matching caveat as getTracksMapLayerFeatures.
  getMapLayerFeatureCount = name => {
    const mapLayer = (this.props.mapLayers || []).find(
      layer =>
        layer && layer.name === name && String(layer.projectId) === 'default'
    );
    return Array.isArray(mapLayer && mapLayer.features)
      ? mapLayer.features.length
      : 0;
  };

  // 'Full tracks - standard' needs the real GPS route per track, but the
  // bulk Tracks fetch only ever returns a coarse start/end stub. Fetch the
  // full trace for every visible-but-uncached track, in parallel, deduped
  // so the same track is never requested twice.
  refreshFullTrackTraces = () => {
    const trackDisplayMode = this.getTrackDisplayMode();
    if (trackDisplayMode !== 'full' && trackDisplayMode !== 'fullColour')
      return;
    if (!this.props.selectedLayers.includes('Tracks')) return;
    if (!this.props.getTrackFullTrace) return;

    const features = this.getTracksMapLayerFeatures();
    const cache = this.props.fullTrackTraces || {};
    if (!this._fullTrackTraceRequests) {
      this._fullTrackTraceRequests = new Set();
    }

    features.forEach(feature => {
      const trackId = this.getTrackFeatureId(feature);
      if (!trackId) return;
      if (cache[trackId]) return;
      if (this._fullTrackTraceRequests.has(trackId)) return;

      this._fullTrackTraceRequests.add(trackId);
      this.setTrackFullTraceLoading(true);
      Promise.resolve(this.props.getTrackFullTrace(trackId)).finally(() => {
        this._fullTrackTraceRequests.delete(trackId);
        if (this._fullTrackTraceRequests.size === 0) {
          this.setTrackFullTraceLoading(false);
        }
      });
    });
  };

  // Reuses the existing "map layer hydrating" indicator (top bar + sweeping
  // progress line, see hasLayerLoading/loadingMapLayers in render()) so
  // fetching every track's full trace shows the same familiar feedback as
  // any other layer load.
  setTrackFullTraceLoading = isLoading => {
    this.setState(prevState => ({
      loadingMapLayers: {
        ...prevState.loadingMapLayers,
        Tracks: isLoading
      }
    }));
  };

  // Debounced wrapper so rapid pan/zoom doesn't refire the same batch of
  // requests (mirrors debouncedRefreshHeatmaps).
  debouncedRefreshFullTrackTraces = () => {
    if (this._fullTrackTraceRefreshTimer) {
      clearTimeout(this._fullTrackTraceRefreshTimer);
    }
    this._fullTrackTraceRefreshTimer = setTimeout(() => {
      this.refreshFullTrackTraces();
    }, 350);
  };

  // Triggers a MarkLogic box-grid fetch for one layer (Observations/Tracks)
  // over the current bounds + filters. Result lands in the layer slice and is
  // pushed to the map via syncHeatmapSource (see componentDidUpdate).
  refreshHeatmap = layerName => {
    const config = HEATMAP_LAYER_CONFIG[layerName];
    if (!config || !config.isShown(this.props)) return;
    if (!this.props.selectedLayers.includes(layerName)) return;
    if (this.props.getLayerHeatmap) this.props.getLayerHeatmap(layerName);
  };

  // Refetches every currently-active heatmap (pan/zoom, filter or resolution
  // change).
  refreshActiveHeatmaps = () => {
    Object.keys(HEATMAP_LAYER_CONFIG).forEach(layerName =>
      this.refreshHeatmap(layerName)
    );
  };

  // Debounced wrapper so rapid viewport changes collapse into one fetch.
  debouncedRefreshHeatmaps = () => {
    if (this._heatmapRefreshTimer) clearTimeout(this._heatmapRefreshTimer);
    this._heatmapRefreshTimer = setTimeout(() => {
      this.refreshActiveHeatmaps();
    }, 350);
  };

  // Pushes the stored grid GeoJSON for a layer into its map source.
  syncHeatmapSource = layerName => {
    const heatmaps = this.props.heatmaps || {};
    this.updateHeatmapSource(
      layerName,
      heatmaps[layerName] || { type: 'FeatureCollection', features: [] }
    );
  };

  // Single source of truth for a heatmap-capable layer's on-map visibility:
  // the heatmap grid shows only when the layer is selected AND in heatmap
  // mode; its markers/centerpoints show only when selected AND not in heatmap
  // mode. So master show/hide and the display-mode radio can never mix.
  applyHeatmapVisibility = layerName => {
    const config = HEATMAP_LAYER_CONFIG[layerName];
    if (!config) return;
    const isShown = config.isShown(this.props);
    const isSelected = this.props.selectedLayers.includes(layerName);
    this.setHeatmapVisibility(
      layerName,
      isSelected && isShown ? 'visible' : 'none'
    );
    this.setLayerMarkersVisibility(
      layerName,
      isSelected && !isShown ? 'visible' : 'none'
    );
  };

  // Changes the grid resolution (componentDidUpdate then refetches).
  changeHeatmapResolution = (layerName, resolution) => {
    const value = Number(resolution);
    if (layerName === 'Tracks') {
      if (this.props.setTrackHeatmapResolution)
        this.props.setTrackHeatmapResolution(value);
    } else if (this.props.setObservationHeatmapResolution) {
      this.props.setObservationHeatmapResolution(value);
    }
  };

  // Changes the heatmap fill transparency. The slider reads as transparency
  // (0% = fully opaque on the left, 100% = fully see-through on the right),
  // but the map paint-property and the redux state underneath are both
  // opacity - the two are inverses of each other - so invert here, once, at
  // the one place the slider's raw value enters the system. Unlike
  // resolution this needs no refetch - it's a pure paint-property, so update
  // the map straight away for instant feedback and persist to redux so it
  // survives layer recreation (pan/zoom re-adding the source, page reload).
  changeHeatmapOpacity = (layerName, transparencyPercent) => {
    const transparency = Number(transparencyPercent) / 100;
    if (!Number.isFinite(transparency)) return;
    const opacity = 1 - transparency;
    if (layerName === 'Tracks') {
      if (this.props.setTrackHeatmapOpacity)
        this.props.setTrackHeatmapOpacity(opacity);
    } else if (this.props.setObservationHeatmapOpacity) {
      this.props.setObservationHeatmapOpacity(opacity);
    }
    this.setHeatmapOpacity(layerName, opacity);
  };

  // Current observation display mode derived from the two exclusive flags.
  getObservationDisplayMode = () => {
    if (this.props.showObservationHeatmap) return 'heatmap';
    if (this.props.showObservationClusters) return 'clusters';
    return 'individual';
  };

  // Sets one exclusive observation display mode (individual|clusters|heatmap).
  setObservationDisplayMode = mode => {
    if (this.props.setShowObservationClusters)
      this.props.setShowObservationClusters(mode === 'clusters');
    if (this.props.setShowObservationHeatmap)
      this.props.setShowObservationHeatmap(mode === 'heatmap');
  };

  // Current track display mode - centerpoints|full|fullColour|heatmap are
  // mutually exclusive, derived from their respective boolean flags.
  getTrackDisplayMode = () => {
    if (this.props.showTrackHeatmap) return 'heatmap';
    if (this.props.showTrackFullStandard) return 'full';
    if (this.props.showTrackFullColour) return 'fullColour';
    return 'centerpoints';
  };

  // Hour-of-day colour legend ("sky gradient" bar) for the "Full tracks -
  // colour coded" display mode - see utils/trackColorUtil.getHourColor.
  // Single source of truth so it can be shown both in the "Full tracks
  // info" toast and the single-track "Track info" playback toast (a
  // played-back track still uses the same hour-of-day colouring). Only
  // ever relevant in 'fullColour' mode - other modes use unrelated colours
  // (e.g. the yellow query-range markers, the blue standard track line)
  // that this legend doesn't describe.
  // Static hour-of-day colour legend ("sky gradient" bar) for the "Full
  // tracks - colour coded" display mode and the single-track colourCoded
  // preference - see utils/trackColorUtil.getHourColor. The interactive
  // hour-range slider (drag-to-filter) is disabled for now - filtering out
  // a sub-range made the excluded segment render solid black instead of
  // disappearing (root cause not yet pinned down), so showing the colour
  // key without the broken filtering is the safer interim state. The
  // drag-handle plumbing (startTrackHourDrag/onTrackHourDrag/
  // changeTrackHourRange/applyTrackHourRangeFilter) is left in place,
  // unused, so the slider can be re-enabled here once that's fixed.
  renderTrackHourColourLegend = () => {
    if (
      this.getTrackDisplayMode() !== 'fullColour' &&
      !this.state.singleTrackColourCoded
    )
      return null;
    const hourColourLegendGradient = `linear-gradient(to right, ${Array.from(
      { length: 25 },
      (_, hour) => {
        const stopColor = getHourColor(hour === 24 ? 0 : hour);
        return `${stopColor} ${((hour / 24) * 100).toFixed(2)}%`;
      }
    ).join(', ')})`;
    return (
      <div className="track-full-colour-legend">
        <div className="track-full-colour-legend-bar-wrap">
          <div
            className="track-full-colour-legend-bar"
            style={{ background: hourColourLegendGradient }}
          />
        </div>
        <div className="track-full-colour-legend-labels">
          <span>00:00</span>
          <span>06:00</span>
          <span>12:00</span>
          <span>18:00</span>
          <span>24:00</span>
        </div>
      </div>
    );
  };

  // Sets the track display mode (centerpoints|full|fullColour|heatmap).
  setTrackDisplayMode = mode => {
    if (this.props.setShowTrackHeatmap)
      this.props.setShowTrackHeatmap(mode === 'heatmap');
    if (this.props.setShowTrackFullStandard)
      this.props.setShowTrackFullStandard(mode === 'full');
    if (this.props.setShowTrackFullColour)
      this.props.setShowTrackFullColour(mode === 'fullColour');
  };

  // Small resolution picker for the MarkLogic box-grid heatmap, shown inside a
  // layer menu when its Heatmap mode is active.
  renderHeatmapResolutionControl = layerName => {
    const current =
      (layerName === 'Tracks'
        ? this.props.trackHeatmapResolution
        : this.props.observationHeatmapResolution) || 25;
    return (
      <div className="layer-option-sub">
        <span className="mr-1">grid</span>
        <div className="layer-option-chips">
          {[5, 10, 25, 50, 100].map(r => (
            <button
              key={r}
              type="button"
              className={'layer-chip' + (current === r ? ' active' : '')}
              onClick={e => {
                e.stopPropagation();
                this.changeHeatmapResolution(layerName, r);
              }}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Transparency slider (0-100%, left = opaque, right = see-through) for a
  // heatmap-capable layer. Pure paint-property change (see
  // changeHeatmapOpacity/setHeatmapOpacity) - updates instantly, no refetch.
  // Native <input type="range"> so it comes with keyboard support and
  // screen-reader semantics (aria-valuenow etc.) out of the box; paired with
  // a visible numeric readout rather than relying on the thumb position
  // alone. The stored/prop value is opacity, not transparency - see
  // changeHeatmapOpacity for the one place that inverts between the two.
  renderHeatmapOpacityControl = layerName => {
    const rawOpacity =
      layerName === 'Tracks'
        ? this.props.trackHeatmapOpacity
        : this.props.observationHeatmapOpacity;
    const defaultOpacity =
      layerName === 'Tracks'
        ? DEFAULT_TRACK_HEATMAP_OPACITY
        : DEFAULT_OBSERVATION_HEATMAP_OPACITY;
    const currentOpacity =
      typeof rawOpacity === 'number' ? rawOpacity : defaultOpacity;
    const currentPercent = Math.round((1 - currentOpacity) * 100);
    const inputId = 'heatmap-opacity-' + layerName;
    return (
      <div className="layer-option-sub layer-option-opacity">
        <label className="mr-1" htmlFor={inputId}>
          transparency
        </label>
        <input
          id={inputId}
          type="range"
          min={0}
          max={100}
          step={5}
          value={currentPercent}
          aria-label={layerName + ' heatmap transparency'}
          onClick={e => e.stopPropagation()}
          onChange={e => {
            this.changeHeatmapOpacity(layerName, e.target.value);
          }}
        />
        <span className="layer-option-opacity-value">{currentPercent}%</span>
      </div>
    );
  };

  // Observations waffle menu: master show/hide + exclusive display mode.
  renderObservationMenu = shown => {
    const mode = this.getObservationDisplayMode();
    return (
      <>
        <label className="layer-option-toggle">
          <input
            type="checkbox"
            checked={shown}
            onChange={() => {
              this.toggleMapLayer('Observations');
              if (shown) this.closeLayerMenu('Observations');
            }}
          />
          <span>Show observations</span>
        </label>
        <div className="dropdown-divider" />
        <label className="layer-option-radio">
          <input
            type="radio"
            name="observation-display-mode"
            checked={mode === 'individual'}
            disabled={!shown}
            onChange={() => this.setObservationDisplayMode('individual')}
          />
          <span>Individual markers</span>
        </label>
        <label className="layer-option-radio">
          <input
            type="radio"
            name="observation-display-mode"
            checked={mode === 'clusters'}
            disabled={!shown}
            onChange={() => this.setObservationDisplayMode('clusters')}
          />
          <span>Observation clusters</span>
        </label>
        {mode === 'clusters' && (
          <div className="layer-option-sub">
            <span className="mr-1">if &gt;</span>
            <div className="layer-option-chips">
              {[100, 250, 500].map(t => (
                <button
                  key={t}
                  type="button"
                  className={
                    'layer-chip' +
                    (this.props.observationClusterThreshold === t
                      ? ' active'
                      : '')
                  }
                  onClick={e => {
                    e.stopPropagation();
                    this.props.setObservationClusterThreshold(t);
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
            <span className="ml-1">obs</span>
          </div>
        )}
        <label className="layer-option-radio">
          <input
            type="radio"
            name="observation-display-mode"
            checked={mode === 'heatmap'}
            disabled={!shown}
            onChange={() => this.setObservationDisplayMode('heatmap')}
          />
          <span>Observation heatmap</span>
        </label>
        {mode === 'heatmap' &&
          this.renderHeatmapResolutionControl('Observations')}
        {mode === 'heatmap' && this.renderHeatmapOpacityControl('Observations')}
      </>
    );
  };

  // Tracks waffle menu: master show/hide + exclusive display mode.
  renderTrackMenu = shown => {
    const mode = this.getTrackDisplayMode();
    // 'Full tracks' rebuilds every point of the real GPS route for every
    // visible track (see tracksFullStandard.js) - only offered for a short,
    // recent period (Today/Yesterday) to keep that bounded, same reasoning
    // as the colour-coded mode's MAX_COLOUR_CODED_TRACKS ceiling.
    const fullTracksAvailable = isTodayOrYesterdayRange(
      this.props.queryStart,
      this.props.queryEnd
    );
    return (
      <>
        <label className="layer-option-toggle">
          <input
            type="checkbox"
            checked={shown}
            onChange={() => {
              this.toggleMapLayer('Tracks');
              if (shown) this.closeLayerMenu('Tracks');
            }}
          />
          <span>Show tracks</span>
        </label>
        <div className="dropdown-divider" />
        <label className="layer-option-radio">
          <input
            type="radio"
            name="track-display-mode"
            checked={mode === 'centerpoints'}
            disabled={!shown}
            onChange={() => this.setTrackDisplayMode('centerpoints')}
          />
          <span>Track centerpoints</span>
        </label>
        <label
          className={
            'layer-option-radio' +
            (fullTracksAvailable ? '' : ' layer-option-disabled')
          }
        >
          <input
            type="radio"
            name="track-display-mode"
            checked={mode === 'full'}
            disabled={!shown || !fullTracksAvailable}
            onChange={() => this.setTrackDisplayMode('full')}
          />
          <span>Full tracks</span>
          {!fullTracksAvailable && (
            <span className="layer-option-soon">Today/Yesterday only</span>
          )}
        </label>
        <label className="layer-option-radio">
          <input
            type="radio"
            name="track-display-mode"
            checked={mode === 'heatmap'}
            disabled={!shown}
            onChange={() => this.setTrackDisplayMode('heatmap')}
          />
          <span>Track heatmap</span>
        </label>
        {mode === 'heatmap' && this.renderHeatmapResolutionControl('Tracks')}
        {mode === 'heatmap' && this.renderHeatmapOpacityControl('Tracks')}
      </>
    );
  };

  // Programmatically closes a layer's Bootstrap dropdown (used when the
  // master show/hide toggle turns the layer off, so the menu doesn't linger).
  closeLayerMenu = name => {
    const button = document.getElementById('common-layer-menu-' + name);
    if (!button) return;
    button.setAttribute('aria-expanded', 'false');
    const dropdown = button.closest('.dropdown');
    if (dropdown) dropdown.classList.remove('show');
    const menu = dropdown && dropdown.querySelector('.dropdown-menu');
    if (menu) menu.classList.remove('show');
  };

  // Checkbox + label on/off indicator shared by every entry in the layer
  // panel above the map - Agents, and now also Observations/Tracks - kept in
  // one place so the visual on/off control never drifts between layers.
  renderLayerCheckbox = (name, key, shown) => (
    <>
      <input
        id={'common-layer-input-' + key}
        type="checkbox"
        name="rtoggle"
        value={name}
        onChange={() => this.toggleMapLayer(name)}
        checked={shown}
      />
      <label
        className="radio-label pl-1 mb-0 mr-1"
        htmlFor={'common-layer-input-' + key}
      >
        {name}
      </label>
    </>
  );

  // Renders one entry in the layer panel above the map: Agents is just the
  // checkbox; Observations/Tracks add a 3-dot waffle menu next to it (see
  // above) for the exclusive display-mode options.
  renderCommonLayerControl = (item, key) => {
    const name = item.name;
    const shown = this.props.selectedLayers.includes(name);

    if (name !== 'Observations' && name !== 'Tracks') {
      return (
        <div
          key={key}
          className={
            'common-layer-control d-flex align-items-center' +
            (shown ? '' : ' is-off')
          }
        >
          {this.renderLayerCheckbox(name, key, shown)}
        </div>
      );
    }

    const menuId = 'common-layer-menu-' + name;
    return (
      <div
        key={key}
        className={
          'common-layer-control common-layer-control--menu d-flex align-items-center' +
          (shown ? '' : ' is-off')
        }
      >
        {this.renderLayerCheckbox(name, key, shown)}
        <div className="dropdown">
          <button
            className="waffle-button"
            type="button"
            id={menuId}
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
            aria-label={name + ' display options'}
            title={name + ' display options'}
          >
            <span className="material-icons" aria-hidden="true">
              more_vert
            </span>
          </button>
          <div
            className="dropdown-menu dropdown-menu-right layer-options-menu"
            aria-labelledby={menuId}
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              className="layer-options-menu-close"
              aria-label={'Close ' + name + ' display options'}
              title="Close"
              onClick={() => this.closeLayerMenu(name)}
            >
              <span className="material-icons" aria-hidden="true">
                close
              </span>
            </button>
            {name === 'Observations'
              ? this.renderObservationMenu(shown)
              : this.renderTrackMenu(shown)}
          </div>
        </div>
      </div>
    );
  };

  // Set map sources and layers
  setMapSources = () => {
    return sourcesHandlers.setMapSources.call(this);
  };

  setObservationLayer = async mapLayer => {
    return sourcesHandlers.setObservationLayer.call(this, mapLayer);
  };

  setAlertLayer = async mapLayer => {
    return sourcesHandlers.setAlertLayer.call(this, mapLayer);
  };

  setTracksLayer = async mapLayer => {
    return sourcesHandlers.setTracksLayer.call(this, mapLayer);
  };

  setSelectedTrackItem = async (id, trackInTimeline, clickedFeature = null) => {
    let query = new QueryBuilder.SearchQuery().withQueryText(
      'entityId:"' + id + '"'
    );

    const normalizedId = String(id || '').trim();
    const findTimelineTrack = () => {
      const timelineResults = Array.isArray(this.props.timelineResults)
        ? this.props.timelineResults
        : [];
      return timelineResults.find(result => {
        const content = Array.isArray(result?.extracted?.content)
          ? result.extracted.content
          : [];
        const geoFeatureEntry = content.find(
          entry => entry && entry.GeoFeature
        );
        const geoFeature = geoFeatureEntry && geoFeatureEntry.GeoFeature;
        if (!geoFeature) return false;
        const props = geoFeature.properties || {};
        const candidateIds = [
          geoFeature.featureId,
          geoFeature.trackId,
          geoFeature.EntityId,
          props.EntityId,
          props.featureId,
          props.trackId
        ]
          .map(value => String(value || '').trim())
          .filter(Boolean);
        return candidateIds.includes(normalizedId);
      });
    };

    const resolvedTrackInTimeline = trackInTimeline || findTimelineTrack();
    if (resolvedTrackInTimeline) {
      const normalizedSelectedTrackItem = (
        resolvedTrackInTimeline.extracted.content.find(c => c.GeoFeature) || {}
      ).GeoFeature;
      this.props.setSelectedItem(normalizedSelectedTrackItem);
    }

    const requests = [
      this.props.getEntireTrack('track', '0', query),
      this.props.getTrackInfo(id),
      this.props.getTrackObservations(id)
    ];
    if (!resolvedTrackInTimeline) {
      requests.push(this.props.getSingleTrackItem(id));
    }
    await Promise.allSettled(requests);
  };

  bringTrackEndpointsToFront = () => {
    if (!this.map) return;
    if (this.map.getLayer(this.trackEndpointsLayerId)) {
      this.map.moveLayer(this.trackEndpointsLayerId);
    }
  };

  setActualTrackLayer = mapLayer =>
    layerHandlers.setActualTrackLayer.call(this, mapLayer);

  setTrackPlaybackLayer = () => layerHandlers.setTrackPlaybackLayer.call(this);

  refreshTrackPlaybackColors = () =>
    layerHandlers.refreshTrackPlaybackColors.call(this);

  setGeoJsonLayer = mapLayer =>
    layerHandlers.setGeoJsonLayer.call(this, mapLayer);

  setLayerClickListener = mapLayer =>
    layerHandlers.setLayerClickListener.call(this, mapLayer);

  setPersonsLayer = async mapLayer =>
    layerHandlers.setPersonsLayer.call(this, mapLayer);

  setMediaLayer = mapLayer => layerHandlers.setMediaLayer.call(this, mapLayer);

  startLKPTimer = () => {
    if (this.LKPTimer === 0) {
      this.LKPTimer = setInterval(this.countDownLKP, 20000);
    }
  };

  // Counterpart to startLKPTimer - previously missing entirely, so this
  // interval ran forever (including well past logout) with nothing left to
  // clear it. Resets to 0 so a later startLKPTimer() can start it again.
  stopLKPTimer = () => {
    if (this.LKPTimer !== 0) {
      clearInterval(this.LKPTimer);
      this.LKPTimer = 0;
    }
  };

  countDownLKP = () => {
    this.props.getLKPData();
  };

  startTimer() {
    if (this.timer === 0) {
      this.timer = setInterval(this.countDown, 500);
    }
  }

  // Counterpart to startTimer - same missing-cleanup issue as
  // stopLKPTimer above.
  stopTimer = () => {
    if (this.timer !== 0) {
      clearInterval(this.timer);
      this.timer = 0;
    }
  };

  getEntireTrack = async id => {
    let query = new QueryBuilder.SearchQuery().withQueryText(
      'entityId:"' + id + '"'
    );

    await this.props.getEntireTrack('track', '0', query);
    await this.props.getTrackInfo(id);
    this.props.getTrackObservations(id);
  };

  countDown = () => {
    if (
      moment().diff(moment(this.props.LKPUpdate), 'seconds') >=
        this.props.updateInterval &&
      !this.gettingLKP
    ) {
      this.gettingLKP = true;
      if (this.props.selectedItem && this.props.selectedItem.featureId) {
        this.props.getTrackInfo(this.props.selectedItem.featureId);
        this.getEntireTrack(this.props.selectedItem.featureId);
      }
      this.props.fetchAllData({ silent: true });
    }

    if (
      moment().diff(moment(this.props.LKPUpdate), 'seconds') <
        this.props.updateInterval &&
      this.gettingLKP
    ) {
      this.gettingLKP = false;
    }
  };

  toggleMapMode = style => {
    if (style === 'satellite') {
      this.map.setStyle('mapbox://styles/jankees/cleijxlii005901qghunc7bis');
      this.setState({ mapStyle: 'satellite' });
    } else if (style === 'streets') {
      this.map.setStyle('mapbox://styles/jankees/cleijg9zb000t01pn0thsmz0q');
      this.setState({ mapStyle: 'streets' });
    } else if (style === 'grid') {
      this.map.setStyle('mapbox://styles/jankees/cldx1uw20000a01n0xjer8fax');
      this.setState({ mapStyle: 'grid' });
    }
  };

  toggleMapLayer = async clickedLayer =>
    layerHandlers.toggleMapLayer.call(this, clickedLayer);

  collapseResults = async () => {
    await this.setState({ resultsCollapsed: !this.state.resultsCollapsed });
    this.map.resize();
  };

  render() {
    const { ...props } = this.props;
    const resultsCollapsed = Boolean(props.resultsCollapsed);
    let desktopMapClass = '';
    if (props.filterCollapsed && resultsCollapsed) {
      desktopMapClass = 'col-12';
    } else if (props.filterCollapsed && !resultsCollapsed) {
      desktopMapClass = 'col-9';
    } else if (
      !props.filterCollapsed &&
      !props.filterExpanded &&
      resultsCollapsed
    ) {
      desktopMapClass = 'col-10';
    } else if (
      !props.filterCollapsed &&
      props.filterExpanded &&
      resultsCollapsed
    ) {
      desktopMapClass = 'col-9';
    } else if (
      !props.filterCollapsed &&
      !props.filterExpanded &&
      !resultsCollapsed
    ) {
      desktopMapClass = 'col-7';
    } else if (
      !props.filterCollapsed &&
      props.filterExpanded &&
      !resultsCollapsed
    ) {
      desktopMapClass = 'col-6';
    }

    const mapClassName =
      desktopMapClass +
      (props.isMobile ? ' mobile-pane mobile-map-pane' : '') +
      (props.isMobile && !props.mobileActive ? ' mobile-pane-hidden' : '');
    const hasCustomSidePanelWidth =
      !props.isMobile &&
      (resultsCollapsed ||
        Number.isFinite(Number(props.filterPanelWidthPx)) ||
        Number.isFinite(Number(props.resultsPanelWidthPx)));
    const mapPaneStyle = hasCustomSidePanelWidth
      ? {
          height: '100%',
          flex: '1 1 auto',
          maxWidth: 'none',
          minWidth: 0
        }
      : { height: '100%' };
    const isMapMaximized =
      this.state.isMapFullscreen || this.state.isMapMaximizedFallback;
    const mapPaneClassName = `${mapClassName}${
      this.state.isMapMaximizedFallback ? ' map-pane-maximized' : ''
    }`;
    const loadingLayerNames = Object.keys(this.state.loadingMapLayers || {})
      .filter(name => this.state.loadingMapLayers[name] === true)
      .sort((a, b) => a.localeCompare(b));
    const hasLayerLoading = loadingLayerNames.length > 0;
    const playbackObservationToasts = Array.isArray(
      this.state.playbackObservationToasts
    )
      ? this.state.playbackObservationToasts
      : [];
    const playbackTrackInfo = this.state.playbackTrackInfo;
    const playbackSpeedText = playbackTrackInfo?.speedDisplay || '--';
    const trackDisplayMode = this.getTrackDisplayMode();
    const showFullTracksInfo =
      (trackDisplayMode === 'full' || trackDisplayMode === 'fullColour') &&
      this.props.selectedLayers.includes('Tracks') &&
      !this.state.fullTracksInfoDismissed &&
      // Both toasts are pinned to the same bottom-right spot
      // (.track-playback-time-toast) - an individual track replay in
      // progress is the more specific/urgent thing to show, so don't
      // double them up while it's active.
      !playbackTrackInfo;
    const fullTracksVisibleFeatures = showFullTracksInfo
      ? this.getTracksMapLayerFeatures()
      : [];
    // Agents come from the same list the Filter panel's "Agent list" count
    // uses (state.timeline.agents) - the Agents *map layer* is a different,
    // map-bounds-scoped thing and isn't what "how many agents" means here.
    const fullTracksAgentsCount = showFullTracksInfo
      ? (this.props.agentsList || []).length
      : 0;
    const fullTracksTracksCount = fullTracksVisibleFeatures.length;
    // "Traces" = total number of GPS points across every loaded full trace
    // (LineString/MultiPoint/MultiLineString coordinate count), not a count
    // of tracks - i.e. how many individual fixes have been fetched so far.
    const countTracePoints = geometry => {
      if (!geometry || !Array.isArray(geometry.coordinates)) return 0;
      if (geometry.type === 'LineString' || geometry.type === 'MultiPoint') {
        return geometry.coordinates.length;
      }
      if (geometry.type === 'MultiLineString') {
        return geometry.coordinates.reduce(
          (sum, line) => sum + (Array.isArray(line) ? line.length : 0),
          0
        );
      }
      return 0;
    };
    const fullTracksTracesCount = showFullTracksInfo
      ? fullTracksVisibleFeatures.reduce((total, feature) => {
          const trackId = this.getTrackFeatureId(feature);
          const fullResponse =
            trackId && (this.props.fullTrackTraces || {})[trackId];
          const responseFeatures = Array.isArray(
            fullResponse && fullResponse.features
          )
            ? fullResponse.features
            : [];
          const trackPointCount = responseFeatures.reduce(
            (sum, f) => sum + countTracePoints(f && f.geometry),
            0
          );
          return total + trackPointCount;
        }, 0)
      : 0;
    // "Kilometers" = total ground distance covered by every loaded full
    // trace, summed consecutive-point-to-consecutive-point (same haversine
    // helper used for track speed/segment distance elsewhere - see
    // utils/locationUtil.haversineMeters).
    const sumGeometryDistanceMeters = geometry => {
      if (!geometry || !Array.isArray(geometry.coordinates)) return 0;
      const sumPath = coordinates => {
        if (!Array.isArray(coordinates) || coordinates.length < 2) return 0;
        let meters = 0;
        for (let i = 1; i < coordinates.length; i++) {
          meters += haversineMeters(coordinates[i - 1], coordinates[i]);
        }
        return meters;
      };
      if (geometry.type === 'LineString' || geometry.type === 'MultiPoint') {
        return sumPath(geometry.coordinates);
      }
      if (geometry.type === 'MultiLineString') {
        return geometry.coordinates.reduce(
          (sum, line) => sum + sumPath(line),
          0
        );
      }
      return 0;
    };
    const fullTracksKilometers = showFullTracksInfo
      ? fullTracksVisibleFeatures.reduce((total, feature) => {
          const trackId = this.getTrackFeatureId(feature);
          const fullResponse =
            trackId && (this.props.fullTrackTraces || {})[trackId];
          const responseFeatures = Array.isArray(
            fullResponse && fullResponse.features
          )
            ? fullResponse.features
            : [];
          const trackMeters = responseFeatures.reduce(
            (sum, f) => sum + sumGeometryDistanceMeters(f && f.geometry),
            0
          );
          return total + trackMeters;
        }, 0) / 1000
      : 0;
    return (
      <div
        ref={this.mapPaneRef}
        className={mapPaneClassName}
        style={mapPaneStyle}
      >
        <div ref={el => (this.mapContainer = el)} className="map-container" />
        {this.state.colourModeFallbackNotice && (
          <div className="track-colour-fallback-notice" role="status">
            <p className="track-colour-fallback-notice-text">
              Switched to <strong>Full tracks</strong>: colour coding stops
              being readable with{' '}
              {this.state.colourModeFallbackNotice.trackCount} tracks on screen
              at once (max {tracksFullColourHandlers.MAX_COLOUR_CODED_TRACKS}).
              Too many overlapping coloured lines blend together and the timing
              pattern they're meant to show gets lost - it also gets heavier to
              render. Narrow your selection (fewer tracks, a shorter period, or
              a smaller area) to use colour coding again.
            </p>
            <button
              type="button"
              className="track-colour-fallback-notice-close"
              title="Close"
              aria-label="Close notice"
              onClick={this.dismissColourModeFallbackNotice}
            >
              &times;
            </button>
          </div>
        )}
        {this.state.centerpointsFallbackNotice && (
          <div className="track-colour-fallback-notice" role="status">
            <p className="track-colour-fallback-notice-text">
              Switched to <strong>Track heatmap</strong>: track centerpoints
              stops being readable and heavy to render with{' '}
              {this.state.centerpointsFallbackNotice.trackCount} tracks on
              screen at once (max {actualTracksHandlers.MAX_CENTERPOINT_TRACKS}
              ). Narrow your selection (fewer tracks, a shorter period, or a
              smaller area) to use centerpoints again.
            </p>
            <button
              type="button"
              className="track-colour-fallback-notice-close"
              title="Close"
              aria-label="Close notice"
              onClick={this.dismissCenterpointsFallbackNotice}
            >
              &times;
            </button>
          </div>
        )}
        {hasLayerLoading && (
          <div className="map-layer-loading-top" aria-live="polite">
            <span className="map-layer-loading-label">
              Loading map layer{loadingLayerNames.length > 1 ? 's' : ''}
            </span>
            <span className="map-layer-hydration-line" aria-hidden="true">
              <span className="map-layer-hydration-progress" />
            </span>
          </div>
        )}
        {(props.showProcessingStatus ||
          Boolean(props.networkQualityToastText)) && (
          <div className="map-info-status">
            {props.showProcessingStatus && (
              <div className="map-processing-status-wrap">
                <div className="search-request-status">
                  {props.processingStatusText}
                </div>
                <span className="map-info-hydration-line" aria-hidden="true">
                  <span className="map-info-hydration-progress" />
                </span>
              </div>
            )}
            {props.networkQualityToastText && (
              <div
                className={`search-request-status network-quality-toast network-quality-${props.networkQuality ||
                  'unknown'}`}
              >
                {props.networkQualityToastText}
              </div>
            )}
          </div>
        )}
        {playbackTrackInfo && (
          <div className="track-playback-time-toast" role="status">
            <div className="track-playback-time-header">
              <p className="track-playback-time-label mb-0">Track info</p>
              <button
                type="button"
                className="track-playback-time-close"
                title="Close"
                aria-label="Close track info"
                onClick={() => this.setState({ playbackTrackInfo: null })}
              >
                <i className="material-icons" aria-hidden="true">
                  close
                </i>
              </button>
            </div>
            <p className="track-playback-time-meta mb-0">
              Clock time: {playbackTrackInfo.timeLabel}
            </p>
            <p className="track-playback-time-meta mb-0">
              Elapsed time: {playbackTrackInfo.elapsedLabel}
            </p>
            <p className="track-playback-time-meta mb-0">
              Elapsed distance: {playbackTrackInfo.distanceLabel}
            </p>
            <p className="track-playback-time-meta mb-0">
              Estimated speed:{' '}
              <span className="track-playback-emphasis">
                {playbackSpeedText} km/h
              </span>
            </p>
            <p className="track-playback-time-meta mb-0">
              Location: {playbackTrackInfo.coordinateLabel}
              <button
                type="button"
                className="track-playback-time-copy"
                title="Copy track info"
                aria-label="Copy track info"
                onClick={() => this.copyPlaybackTrackInfo(playbackTrackInfo)}
              >
                <i className="material-icons" aria-hidden="true">
                  content_copy
                </i>
              </button>
            </p>
            {this.renderTrackHourColourLegend()}
          </div>
        )}
        {showFullTracksInfo && (
          <div className="track-playback-time-toast" role="status">
            <div className="track-playback-time-header">
              <p className="track-playback-time-label mb-0">Full tracks info</p>
              <button
                type="button"
                className="track-playback-time-close"
                title="Close"
                aria-label="Close full tracks info"
                onClick={() => this.setState({ fullTracksInfoDismissed: true })}
              >
                <i className="material-icons" aria-hidden="true">
                  close
                </i>
              </button>
            </div>
            <p className="track-playback-time-meta mb-0">
              Agents: {fullTracksAgentsCount}
            </p>
            <p className="track-playback-time-meta mb-0">
              Tracks: {fullTracksTracksCount}
            </p>
            <p className="track-playback-time-meta mb-0">
              Kilometers: {Math.round(fullTracksKilometers)} km
            </p>
            <p className="track-playback-time-meta mb-0">
              Traces: {fullTracksTracesCount}
            </p>
            {this.renderTrackHourColourLegend()}
          </div>
        )}
        {!playbackTrackInfo &&
          !showFullTracksInfo &&
          this.state.singleTrackColourCoded &&
          !this.state.singleTrackColourLegendDismissed && (
            <div className="track-playback-time-toast" role="status">
              <div className="track-playback-time-header">
                <p className="track-playback-time-label mb-0">
                  Track colour legend
                </p>
                <button
                  type="button"
                  className="track-playback-time-close"
                  title="Close"
                  aria-label="Close track colour legend"
                  onClick={() =>
                    this.setState({ singleTrackColourLegendDismissed: true })
                  }
                >
                  <i className="material-icons" aria-hidden="true">
                    close
                  </i>
                </button>
              </div>
              {this.renderTrackHourColourLegend()}
            </div>
          )}
        {playbackObservationToasts.length > 0 && (
          <div className="track-playback-observation-toast-stack" role="status">
            {playbackObservationToasts.map(toast => (
              <div key={toast.id} className="track-playback-observation-toast">
                <div className="track-playback-observation-head mb-1">
                  <p className="track-playback-observation-title mb-0">
                    {toast.timeLabel && (
                      <strong className="track-playback-observation-time">
                        {toast.timeLabel}
                      </strong>
                    )}{' '}
                    {toast.observationType}
                  </p>
                  {toast.observationTypeIcon && (
                    <img
                      src={toast.observationTypeIcon}
                      alt=""
                      className="track-playback-observation-type-icon"
                      aria-hidden="true"
                    />
                  )}
                </div>
                <p className="track-playback-observation-description mb-2">
                  {toast.description || 'No description'}
                </p>
                <div className="track-playback-observation-badges">
                  {(toast.conceptBadges || []).map((badge, index) => (
                    <span
                      key={`${toast.id}-${badge}-${index}`}
                      className="badge badge-secondary p-2 mr-1 mb-1 text-uppercase"
                    >
                      {badge}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="custom-control-container">
          <button id="button_circle">
            <svg height="15" width="20">
              <circle
                cx="8"
                cy="8"
                r="6"
                stroke="black"
                strokeWidth="1.3"
                fill="none"
              />
              O
            </svg>
          </button>
          <button id="button_rectangle" className=" border-top">
            <svg height="15" width="20">
              <rect
                x="2"
                y="1"
                width="11"
                height="11"
                stroke="black"
                strokeWidth="1.3"
                fill="none"
              />
              \ R
            </svg>
          </button>
        </div>

        <div className="dropdown dropleft" id="map-menu">
          <button
            className="svg-button"
            type="button"
            id="dropdownMenuButton"
            data-toggle="dropdown"
            aria-expanded="false"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M23 13H13v10h10zm-9 9v-5h5v2h-2v1h2v2zm8 0h-2v-5h2zm0-6h-8v-2h8zM11 1H1v10h10zm-.519 7.085l-.1-.008c-.133-.01-.252-.039-.381-.056V10H5.956c.019.067.043.13.058.2H4.981c-.023-.071-.062-.131-.089-.2H2V7.266a3.707 3.707 0 0 0-.108-.046l-.093-.035-.166-1.129.367.138V2h2.053a7.315 7.315 0 0 1-.094-.422l-.016-.1.989-.155.015.1c.007.04.042.254.126.577H10v5.014c.152.024.299.054.46.067l.1.008zm-.021-1.004l.1.008-.079.996-.1-.008c-.133-.01-.252-.039-.381-.056C5.759 7.455 4.385 3.332 4.053 2a7.315 7.315 0 0 1-.094-.422l-.016-.1.989-.155.015.1c.007.04.042.254.126.577C5.42 3.328 6.603 6.488 10 7.014c.152.024.299.054.46.067zM5.956 10c.019.067.043.13.058.2H4.981c-.023-.071-.062-.131-.089-.2A5.654 5.654 0 0 0 2 7.266a3.707 3.707 0 0 0-.108-.046l-.093-.035-.166-1.129.611.229c.14.052 2.995 1.168 3.712 3.715zM23 9V1H13v10h10zm-1-7v6h-4V7h2V5h1V2zm-3 3v1h-5V4h3v1zm1-3v2h-2V2zm-6 0h3v1h-3zm0 8V7h3v2h5v1zM1 23h10V13H1zm1-1v-1.614A4.076 4.076 0 0 0 3.313 20a2.44 2.44 0 0 0 .6-1.413c.125-1.22.36-1.595 1.65-1.586a1.976 1.976 0 0 1 1.8 1.003c1.01.879 1.552 1.282 2.292 1.048a3.123 3.123 0 0 1 .345-.08V22zm8-8v3.937a9.113 9.113 0 0 0-.646.161c-.501.159-.765-.247-1.528-.99a2.738 2.738 0 0 0-2.224-1.066 2.538 2.538 0 0 0-2.39 1.045c-.306.453.01 1.248-.5 2.038a1.199 1.199 0 0 1-.712.192V14z" />
              <path fill="none" d="M0 0h24v24H0z" />
            </svg>
          </button>
          <div className="dropdown-menu" aria-labelledby="dropdownMenuButton">
            <div className="dropdown-item">
              <input
                id="streets-v11"
                type="radio"
                name="rtoggle"
                value="streets"
                onChange={() => this.toggleMapMode('streets')}
                checked={this.state.mapStyle === 'streets'}
              />
              <label className="radio-label" htmlFor="streets-v11">
                Map
              </label>
            </div>
            <div className="dropdown-item">
              <input
                id="satellite-v9"
                type="radio"
                name="rtoggle"
                value="satellite"
                onChange={() => this.toggleMapMode('satellite')}
                checked={this.state.mapStyle === 'satellite'}
              />
              <label className="radio-label" htmlFor="satellite-v9">
                Satellite
              </label>
            </div>
            <div className="dropdown-item">
              <input
                id="grid-v9"
                type="radio"
                name="rtoggle"
                value="grid"
                onChange={() => this.toggleMapMode('grid')}
                checked={this.state.mapStyle === 'grid'}
              />
              <label className="radio-label" htmlFor="grid-v9">
                Outdoors
              </label>
            </div>
          </div>
        </div>

        {props.mapLayers.filter(
          item =>
            item.projectId !== '123456' &&
            item.name !== 'Observations' &&
            item.name !== 'Tracks' &&
            item.name !== 'Agents'
        ).length > 0 && (
          <div id="layers-menu" className="dropdown dropleft">
            <button
              className="svg-button"
              type="button"
              id="dropdownMenuButton"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <svg width="20" height="20" viewBox="0 0 900.000000 800.000000">
                <g
                  transform="translate(0.000000,800.000000) scale(0.100000,-0.100000)"
                  fill="#000000"
                  stroke="none"
                >
                  <path
                    d="M2255 6874 c-1227 -614 -2234 -1119 -2238 -1124 -5 -4 1002 -513
2238 -1131 l2246 -1123 2245 1122 c1235 617 2243 1126 2240 1130 -8 13 -4471
2242 -4487 2241 -8 0 -1017 -502 -2244 -1115z m3748 -375 l1502 -752 -1503
-751 -1502 -751 -1502 751 -1503 751 1500 751 c825 413 1501 751 1503 751 1 1
678 -337 1505 -750z"
                  />
                  <path
                    d="M453 4287 c-244 -122 -443 -224 -443 -227 0 -3 1010 -510 2245 -1128
l2245 -1122 2245 1122 c1235 618 2245 1125 2245 1128 0 3 -200 105 -445 228
l-445 222 -1800 -900 -1800 -900 -1800 900 c-990 495 -1801 900 -1802 899 -2
0 -202 -100 -445 -222z"
                  />
                  <path
                    d="M453 2597 c-244 -122 -443 -224 -443 -227 0 -3 1011 -510 2246 -1127
l2245 -1123 2244 1123 c1235 617 2245 1125 2245 1129 0 3 -201 106 -446 229
l-447 222 -1793 -896 c-987 -494 -1798 -897 -1804 -897 -5 0 -816 403 -1800
895 -984 492 -1793 895 -1797 895 -5 0 -207 -100 -450 -223z"
                  />
                </g>
              </svg>
            </button>
            <div
              className="dropdown-menu"
              aria-labelledby="dropdownMenuButton"
              style={{ maxHeight: '250px', overflow: 'scroll' }}
            >
              {_.orderBy(
                _.sortBy(
                  props.mapLayers.filter(
                    item =>
                      item.projectId !== '123456' &&
                      item.name !== 'Observations' &&
                      item.name !== 'Tracks' &&
                      item.name !== 'Agents'
                  ),
                  function(sortItem) {
                    return sortItem.projectId === 'default' &&
                      (sortItem.id === 0 || sortItem.id === 1)
                      ? 0
                      : 1;
                  }
                ),
                [mapLayer => mapLayer.name.toLowerCase()],
                ['asc']
              ).map((item, key) => (
                <div key={key} className="dropdown-item">
                  <input
                    id={'layer-input-' + key}
                    type="checkbox"
                    name="rtoggle"
                    value={item.name}
                    onChange={() => this.toggleMapLayer(item.name)}
                    checked={this.props.selectedLayers.includes(item.name)}
                  />
                  <label className="radio-label" htmlFor={'layer-input-' + key}>
                    {item.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {this.state.isMeasuring && (
          <div className="distance">
            <span>{this.state.distance}</span>
          </div>
        )}

        <div id="measure-menu" className="dropdown dropleft">
          <button
            className="svg-button"
            type="button"
            onClick={() => this.toggleMeasure()}
          >
            <span>📏</span>
          </button>
        </div>

        <div id="maximize-map-menu" className="dropdown dropleft">
          <button
            className="svg-button"
            type="button"
            onClick={this.toggleMapMaximize}
            aria-label={isMapMaximized ? 'Restore map size' : 'Maximize map'}
            title={isMapMaximized ? 'Normal size' : 'Maximize map'}
          >
            <span
              className="material-icons map-maximize-icon"
              aria-hidden="true"
            >
              {isMapMaximized ? 'fullscreen_exit' : 'fullscreen'}
            </span>
          </button>
        </div>

        {this.map && (
          <MapPrint
            map={this.map}
            selectedLayers={this.props.selectedLayers}
            mapLayers={props.mapLayers}
            currentUser={props.currentUser}
          />
        )}

        {this.map && this.props.showGrid && <GraticuleOverlay map={this.map} />}

        <div id="layers-menu-common" className="p-2 mr-3">
          {_.sortBy(
            props.mapLayers.filter(
              item =>
                item.projectId !== '123456' &&
                (item.name === 'Observations' ||
                  item.name === 'Tracks' ||
                  item.name === 'Agents')
            ),
            function(sortItem) {
              return sortItem.projectId === 'default' &&
                (sortItem.id === 0 || sortItem.id === 1)
                ? 0
                : 1;
            }
          ).map((item, key) => this.renderCommonLayerControl(item, key))}
        </div>

        {this.props.showCoordinates && (
          <MouseLocation
            ref={this.mouseLocationRef}
            lat={this.state.mouseLat}
            long={this.state.mouseLong}
          />
        )}

        <div
          aria-live="polite"
          aria-atomic="true"
          className="d-flex justify-content-center align-items-center"
        >
          <div
            className={
              this.state.toastOpen
                ? 'toast custom-toast show'
                : 'toast custom-toast hide'
            }
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <div className="toast-header">
              <strong className="mr-auto">Audio</strong>
              <button
                type="button"
                className="ml-2 mb-1 close"
                data-dismiss="toast"
                aria-label="Close"
                onClick={() => this.setState({ toastOpen: false })}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="toast-body custom-toast-body">
              <span className="toast-description">
                Allow audio to play for Alert notifications
              </span>
              <button
                type="button"
                className="btn btn-primary btn-sm float-right custom-btn"
                onClick={() => {
                  this.props.allowAlertSounds();
                  this.setState({ toastOpen: false });
                }}
              >
                Yes
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm float-right custom-btn"
                onClick={() => this.setState({ toastOpen: false })}
              >
                No
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const getMapLayers = state => state.layer.layers ?? [];
const getSelectedLayers = state => state.layer.selectedLayers || [];
const getTrackLayer = state => state.layer.trackLayer || {};
const getSelectedLanguage = state => state.ontology.selectedLanguage || 'en';
const getUpdateInterval = state => state.ontology.updateInterval || 300;
const getSelectedAreas = state => state.query.areas || [];
const getTimelineResults = state => state.timeline.results || [];
const getTimelineTotal = state => state.timeline.total || 0;
const getSelectedItem = state => state.timeline.selectedItem || {};
const getTimelineSelectedTrackObservations = state =>
  state.timeline.selectedTrackObservations || [];
const getLKPUpdate = state => state.user.lastKnownPosition;
const getAlertFeatures = state => state.layer.alertFeatures || [];
const getAlertTimelineOpen = state => state.ontology.alertTimelineOpen || false;
const getMounted = state => state.timeline.mounted || false;
const getShowAgentNames = state =>
  state.ontology.showAgentNames === undefined
    ? true
    : state.ontology.showAgentNames;
const getHideOldAgents = state => state.ontology.hideOldAgents || false;
const getShowObservationClusters = state =>
  state.ontology.showObservationClusters || false;
const getShowObservationHeatmap = state =>
  state.ontology.showObservationHeatmap || false;
const getShowTrackHeatmap = state => state.ontology.showTrackHeatmap || false;
const getShowTrackFullStandard = state =>
  state.ontology.showTrackFullStandard || false;
const getShowTrackFullColour = state =>
  state.ontology.showTrackFullColour || false;
const getHeatmaps = state => state.layer.heatmaps || {};
const getFullTrackTraces = state => state.layer.fullTrackTraces || {};
const getObservationHeatmapResolution = state =>
  state.ontology.observationHeatmapResolution || 25;
const getTrackHeatmapResolution = state =>
  state.ontology.trackHeatmapResolution || 25;
// Opacity 0 (transparency 100%, fully see-through) is a valid, deliberately
// chosen value - `|| 0.3` would treat it as falsy and silently snap back to
// the 0.3 default (the exact bug that made 100% transparency unreachable),
// so check the type instead.
const getObservationHeatmapOpacity = state =>
  typeof state.ontology.observationHeatmapOpacity === 'number'
    ? state.ontology.observationHeatmapOpacity
    : DEFAULT_OBSERVATION_HEATMAP_OPACITY;
const getTrackHeatmapOpacity = state =>
  typeof state.ontology.trackHeatmapOpacity === 'number'
    ? state.ontology.trackHeatmapOpacity
    : DEFAULT_TRACK_HEATMAP_OPACITY;
const getSelectedAgents = state => state.query.agents || [];
const getNetworkQuality = state => state.network?.quality || 'unknown';
const getNetworkQualityToastText = state => state.network?.toast?.text || '';
const getTrackColor = state => state.ontology?.trackColor || 'yellow';
const getObservationClusterThreshold = state =>
  state.ontology?.observationClusterThreshold || 500;
const getCurrentUser = state => state.user.currentUser || '';
const getShowGrid = state => state.ontology.showGrid || false;
const getShowCoordinates = state => state.ontology.showCoordinates || false;
const getCamtrapResults = state => state.layer.camtrapResults || [];
const getAgentsList = state => state.timeline.agents || [];
const getQueryStart = state => state.query.start;
const getQueryEnd = state => state.query.end;
const mapStateToProps = createSelector(
  getMapLayers,
  getSelectedLayers,
  getTrackLayer,
  getSelectedLanguage,
  getSelectedAreas,
  getTimelineResults,
  getTimelineTotal,
  getSelectedItem,
  getTimelineSelectedTrackObservations,
  getLKPUpdate,
  getUpdateInterval,
  getAlertFeatures,
  getAlertTimelineOpen,
  getMounted,
  getShowAgentNames,
  getHideOldAgents,
  getShowObservationClusters,
  getShowObservationHeatmap,
  getShowTrackHeatmap,
  getShowTrackFullStandard,
  getShowTrackFullColour,
  getHeatmaps,
  getFullTrackTraces,
  getObservationHeatmapResolution,
  getTrackHeatmapResolution,
  getObservationHeatmapOpacity,
  getTrackHeatmapOpacity,
  getSelectedAgents,
  getNetworkQuality,
  getNetworkQualityToastText,
  getTrackColor,
  getObservationClusterThreshold,
  getCurrentUser,
  getShowGrid,
  getShowCoordinates,
  getCamtrapResults,
  getAgentsList,
  getQueryStart,
  getQueryEnd,

  (
    mapLayers,
    selectedLayers,
    trackLayer,
    selectedLanguage,
    selectedAreas,
    timelineResults,
    timelineTotal,
    selectedItem,
    timelineSelectedTrackObservations,
    LKPUpdate,
    updateInterval,
    alertFeatures,
    alertTimelineOpen,
    mounted,
    showAgentNames,
    hideOldAgents,
    showObservationClusters,
    showObservationHeatmap,
    showTrackHeatmap,
    showTrackFullStandard,
    showTrackFullColour,
    heatmaps,
    fullTrackTraces,
    observationHeatmapResolution,
    trackHeatmapResolution,
    observationHeatmapOpacity,
    trackHeatmapOpacity,
    selectedAgents,
    networkQuality,
    networkQualityToastText,
    trackColor,
    observationClusterThreshold,
    currentUser,
    showGrid,
    showCoordinates,
    camtrapResults,
    agentsList,
    queryStart,
    queryEnd
  ) => ({
    mapLayers,
    selectedLayers,
    trackLayer,
    selectedLanguage,
    selectedAreas,
    timelineResults,
    timelineTotal,
    selectedItem,
    timelineSelectedTrackObservations,
    LKPUpdate,
    updateInterval,
    alertFeatures,
    alertTimelineOpen,
    mounted,
    showAgentNames,
    hideOldAgents,
    showObservationClusters,
    showObservationHeatmap,
    showTrackHeatmap,
    showTrackFullStandard,
    showTrackFullColour,
    heatmaps,
    fullTrackTraces,
    observationHeatmapResolution,
    trackHeatmapResolution,
    observationHeatmapOpacity,
    trackHeatmapOpacity,
    selectedAgents,
    networkQuality,
    networkQualityToastText,
    trackColor,
    observationClusterThreshold,
    currentUser,
    showGrid,
    showCoordinates,
    camtrapResults,
    agentsList,
    queryStart,
    queryEnd
  })
);

const mapDispatchToProps = {
  getFilterTreeList: actions.ontology.getFilterTreeList,
  getLabels: actions.ontology.getLabels,
  getAllLayerFeatures: actions.user.getAllLayerFeatures,
  getAlertLayerFeature: actions.user.getAlertLayerFeature,
  getAgentsLayerFeature: actions.user.getAgentsLayerFeature,
  clearEntireTrack: actions.layer.clearEntireTrack,
  clearSelectedItem: actions.timeline.clearSelectedItem,
  getLayerFeatures: actions.user.getLayerFeatures,
  setSelectedLayers: actions.layer.setSelectedLayers,
  getSpecificLayer: actions.user.getSpecificLayer,
  setMapCoordinates: actions.query.setMapCoordinates,
  setSelectedAreas: actions.query.setSelectedAreas,
  setSelectedItem: actions.timeline.setSelectedItem,
  getEntireTrack: actions.layer.getEntireTrack,
  getTrackFullTrace: actions.layer.getTrackFullTrace,
  getTrackInfo: actions.layer.getTrackInfo,
  getSingleTrackItem: actions.timeline.getSingleTrackItem,
  getSingleItem: actions.timeline.getSingleItem,
  getTrackObservations: actions.layer.getTrackObservations,
  allowAlertSounds: actions.timeline.allowAlertSounds,
  fetchClusterResults: actions.layer.fetchClusterResults,
  clearCamtrapFocus: actions.layer.clearCamtrapFocus,
  setShowObservationHeatmap: actions.ontology.setShowObservationHeatmap,
  setShowTrackHeatmap: actions.ontology.setShowTrackHeatmap,
  setShowTrackFullStandard: actions.ontology.setShowTrackFullStandard,
  setShowTrackFullColour: actions.ontology.setShowTrackFullColour,
  getLayerHeatmap: actions.user.getLayerHeatmap,
  setObservationHeatmapResolution:
    actions.ontology.setObservationHeatmapResolution,
  setTrackHeatmapResolution: actions.ontology.setTrackHeatmapResolution,
  setObservationHeatmapOpacity: actions.ontology.setObservationHeatmapOpacity,
  setTrackHeatmapOpacity: actions.ontology.setTrackHeatmapOpacity,
  setShowObservationClusters: actions.ontology.setShowObservationClusters,
  setObservationClusterThreshold:
    actions.ontology.setObservationClusterThreshold
};

export default connect(mapStateToProps, mapDispatchToProps)(MapComponent);
